/**
 * CanvasPageV4 — Prove7 Control Vector's clean-slate Workflow Canvas host.
 *
 * The strangler-fig replacement for CanvasPageV3.jsx (16,713 lines). V4
 * deliberately reuses the proven state machine (`useCanvas`), layout
 * pipeline (`flatToElkHierarchical` via V4CanvasView), and API layer
 * (`services/api`) while shedding:
 *   - The 16k-line monolithic host shell
 *   - The hidden legacy SVG canvas tree (V3:6437-6743) mounting per render
 *   - Five overlapping read-only/lock booleans (V3:2392, 2421-2423)
 *   - The dual representation of `insertionTarget` (V3:6517 vs 6651)
 *   - Six `console.warn` paths that swallow auto-save failures (V3 audit P2 #14)
 *
 * What V4 keeps that V3 has right:
 *   - `useCanvas` is a competent state machine. V4 calls it verbatim.
 *   - `flatToElkHierarchical` is the G479-G483 working baseline. V4 uses it.
 *   - V2 node + edge components (rendered through V2_NODE_TYPES) are stable.
 *   - `TaskPicker` works. V4 reuses it (Phase 2).
 *   - Save / load schema is identical so workflows round-trip byte-for-byte.
 *
 * What V4 adds that V3 lacks:
 *   - Per-branch + on case/leg/loop-body containers (Patch D done natively
 *     rather than retrofit — V3 audit identified this as a multi-day patch
 *     in the regression-magnet host file; in V4 it's ~30 LOC).
 *   - Undo / redo (Phase 3 — V3 has none, every destructive action is permanent).
 *   - Copy / paste of nodes (Phase 3 — V3 has none).
 *   - Multi-select with visible highlight (Phase 3 — V3's ⌘A is silent).
 *   - Surfaced graph-repair report (V3's validateAndRepairDAG silently
 *     mutates user graphs; V4 will surface every repair as a toast).
 *
 * Phase progression:
 *   Phase 1 (this file's current state):
 *     Route + flag + minimal DAG render + drag persistence + manual edge
 *     draw + select + "Open in V3" link. Save/load via URL param.
 *   Phase 2: TaskPicker integration, per-leaf +, per-branch + on
 *     case/leg/loop-body, delete-via-key, save round-trip.
 *   Phase 3: Undo/redo, copy/paste, multi-select with viz.
 *   Phase 4: Polish, telemetry, beta flag flip.
 *
 * Out of V4 day-one scope (V3 still owns these via the "Open in V3" link):
 *   AI fix flow, runbook agent banner, execution replay overlay,
 *   GEL external-agent integration, AutoLet CEG read-only locks,
 *   JSON code tab. These ship to V4 in later milestones once core is proven.
 */

import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { ExternalLink, AlertCircle, Save as SaveIcon, AlertTriangle } from 'lucide-react';
import { useCanvas } from '../hooks/useCanvas';
import { useToast } from '../contexts/ToastContext';
// CTX-AUTH — required so createWorkflow can include orgId/tenantId/createdBy
// (BE enforces NOT NULL on workflows.org_id) and the workflow-list call can
// scope by tenant. Mirrors V3 CanvasPageV3.jsx:1310-1316.
import { useAuth } from '../contexts/AuthContext';
import * as api from '../services/api';
import V4CanvasView from './v4/V4CanvasView';
import V4Inspector from './v4/V4Inspector';
// Phase 5b — left-rail node palette. Categorized + searchable, supports
// native HTML5 drag-drop onto the canvas and click-to-add fallback.
import V4NodePalette from './v4/V4NodePalette';
// Phase 7q-1 — left-panel mode router: swaps Palette ↔ Runs tab
import V4LeftPanel, { LEFT_MODES } from './v4/V4LeftPanel';
// Phase 5c-1 — header workflow switcher (dropdown of all workflows + actions)
import V4WorkflowSwitcher from './v4/V4WorkflowSwitcher';
// Phase 5c-2/3 — name dialog used for both New and Save As
import V4WorkflowCreateDialog from './v4/V4WorkflowCreateDialog';
// Phase 5d-1 — read-only JSON code view of the live canvas definition
import V4JsonView from './v4/V4JsonView';
// Phase 6c-3 — universal quick-action picker bound to ⌘K. Combines
// node-insert, workflow-switch, and common actions into one searchable
// surface.
import V4CommandPalette from './v4/V4CommandPalette';
// Phase 7a-2 — recent runs dropdown. Lists last N workflow runs with
// status pill + click-to-open detail panel.
import V4RunsDropdown from './v4/V4RunsDropdown';
// Phase 7a-3 — run detail slideout with Timeline / Output / Logs tabs.
// Auto-polls while the run is non-terminal so the operator sees live
// status updates without a manual refresh.
import V4RunDetailPanel from './v4/V4RunDetailPanel';
// Phase 7b — AI Builder. Universal AI surface (Build / Debug / Import
// modes) anchored bottom-right of the canvas. ⌘I toggles. Phase 7b-1
// ships UI shell only; subsequent sub-phases wire each mode.
import V4AIBuilder from './v4/V4AIBuilder';
// Phase 7c-2 — consolidated toolbar with grouped actions, brand tokens,
// and overflow menu for low-frequency actions. Replaces the inline
// header block that had grown to 7 buttons + 3 statuses across one row.
import V4Toolbar from './v4/V4Toolbar';
// Phase 7c-3 — the single right-rail surface that replaces the 4 floating
// panels (Inspector / Validation / Run Detail / AI Builder). One active
// mode at a time, icon strip on the left edge for switching.
import V4RightRail, { RAIL_MODES } from './v4/V4RightRail';
// Phase 7c-7 — bottom execution dock. Auto-mounts when a run is in
// flight (openRunId is set). Implements the B-half of the A+B hybrid:
// design mode = Calm Pro chrome, watch mode = Mission Control dock.
import V4BottomDock from './v4/V4BottomDock';
// Phase 7g — pre-Run input prompt for workflows declaring required inputs
import V4ExecuteInputModal from './v4/V4ExecuteInputModal';
// Phase 7h — Cognitive Brief slideout (double-click cognitive_loop node)
import V4CognitiveBriefSlideout from './v4/V4CognitiveBriefSlideout';
// Phase 7i — Pre-save governance diff modal + phase regress.
// Fires when api.updateWorkflow returns 409/423 (phase-allowlist block).
import V4GovernanceDiffModal from './v4/V4GovernanceDiffModal';
// Phase 5e — pure validator + UI surfaces (banner / panel)
import { validateWorkflow } from './v4/v4Validate';
import V4ValidationPanel, { V4ValidationBanner } from './v4/V4ValidationPanel';
// Phase 2a — TaskPicker integration. Reuse V3's picker verbatim so
// V4 inherits the proven UX (keyboard nav, tabs, search, pagination)
// without re-implementing. The picker calls onChoose with a normalized
// item; we route to canvas.addTaskToFlow which owns the splice/append
// decision tree.
import TaskPicker from '../components/canvas/TaskPicker';
import { getNodeTypeDef, NODE_TYPES } from '../data/soarNodes';
import { getCuratedRecipes } from '../data/canvasRecipes';
// Phase 4b — fire-and-forget telemetry for V4 user actions. Mirrors V3's
// usage of services/canvasTelemetry (picker_open/row_chosen/picker_close
// stay name-compatible for cross-version comparison) and adds V4-specific
// events with a `v4_` prefix.
import { track as trackTelemetry } from '../services/canvasTelemetry';

// Mirror V3's invariant: at most ONE entry/trigger node per workflow.
// The picker can offer Start; if the canvas already has one, we refuse
// before reaching canvas.addTaskToFlow so the toast is precise.
const SINGLE_INSTANCE_ENTRY_TYPES = new Set(['manual_start', 'start']);

// Phase 5d-1 — tab strip styles. Sits above the canvas/json content.
const tabStripStyle = {
  display: 'flex', alignItems: 'center', gap: 2,
  padding: '6px 14px 0',
  background: '#ffffff',
  borderBottom: '1px solid #e2e8f0',
  flexShrink: 0,
};
const tabButtonStyle = {
  padding: '6px 14px',
  fontSize: 12, fontWeight: 500,
  color: '#64748b',
  background: 'transparent',
  border: 'none',
  borderBottom: '2px solid transparent',
  cursor: 'pointer',
  marginBottom: -1, // sit on top of the strip's bottom border
  fontFamily: 'inherit',
  transition: 'color 100ms ease, border-color 100ms ease',
};
const tabButtonActiveStyle = {
  color: '#0f172a',
  borderBottomColor: '#0f172a',
  fontWeight: 600,
};

// Phase 4a — shared style for keyboard shortcut chips in the hint strip.
const shortcutKbdStyle = {
  display: 'inline-block',
  padding: '2px 5px',
  background: '#f1f5f9',
  border: '0.5px solid #cbd5e1',
  borderRadius: 3,
  fontSize: 10,
  fontFamily: 'SFMono-Regular, Menlo, monospace',
  color: '#0f172a',
  marginRight: 4,
};

export default function CanvasPageV4() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Accept both `?workflowId=` (V3 deep-link convention) and `?id=` (shorter
  // V4 alias) so any bookmark style works.
  const workflowId = searchParams.get('workflowId') || searchParams.get('id');
  const { addToast } = useToast();
  // CTX-AUTH — pull active session's org/tenant/user. V3 reads the same
  // trio (CanvasPageV3:1315-1316 + 2641-2645). Without these, createWorkflow
  // POSTs with org_id=null and BE rejects on the NOT NULL constraint, and
  // getWorkflows() returns HTTP 400 because the tenant filter is missing.
  const { session } = useAuth();
  const orgId    = session?.orgId    || null;
  const tenantId = session?.tenantId || null;
  const userId   = session?.userId   || session?.id || null;
  const canvas = useCanvas({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [workflowMeta, setWorkflowMeta] = useState(null);
  // Phase 2a — TaskPicker insertionTarget state. Discriminated-union shape:
  //   null                                          picker closed
  //   { kind: 'after-node', nodeId }                 + on a leaf
  //   { kind: 'edge', connectionId }                 picker on an edge (Phase 2b)
  //   { kind: 'branch', branchEdgeId, ... }          per-branch + (Phase 2b)
  //   { kind: 'palette' }                            ⌘E with no anchor
  const [insertionTarget, setInsertionTarget] = useState(null);

  // Phase 3a — Undo/redo via snapshot stack. Tracks the last N snapshots of
  // canvas.getCanvasData(). Capture is debounced from payload changes so a
  // drag-stutter doesn't spam the stack; suppressed for ~700ms after an
  // undo/redo replay so the restored state doesn't bounce back into past.
  // Bounded at 50 entries for memory safety.
  const historyRef = useRef({
    past: [],          // [snapshot, snapshot, ...] chronological — oldest first
    future: [],        // [snapshot, snapshot, ...] for redo — most-recent first
    lastCaptured: null,
    suppressUntil: 0,
  });
  // setUndoTick is a re-render trigger so canUndo/canRedo (used as keyboard
  // shortcut gates and potential future button state) stay live.
  const [, setUndoTick] = useState(0);

  // Phase 3b — in-V4 clipboard for ⌘C / ⌘V. Single-node only for now.
  // Multi-node copy/paste lands in Phase 3c once multi-select is wired.
  const [clipboard, setClipboard] = useState(null);

  // ── Phase 5c — workflow create/clone dialog state ────────────────
  // Single dialog used for both New and Save As. `mode` discriminates:
  //   'new'      → POST createWorkflow with seed sentinel definition
  //   'save-as'  → POST createWorkflow with CURRENT canvas as definition
  // The dialog is closed by passing null.
  const [createDialog, setCreateDialog] = useState(null);
  const [createBusy, setCreateBusy] = useState(false);

  // ── Phase 5d-1 — active main-pane tab ────────────────────────────
  // 'canvas' (default) renders V4CanvasView + palette + inspector.
  // 'json'   renders V4JsonView (read-only definition viewer) full-width.
  const [activeTab, setActiveTab] = useState('canvas');

  // ── Phase 5e — validation panel open/closed state ────────────────
  // The banner toggles this; the X in the panel header closes it.
  // Default closed so the canvas isn't covered on every workflow load
  // — operator opts in via the banner's "Show details".
  const [validationPanelOpen, setValidationPanelOpen] = useState(false);

  // ── Phase 5f-4 — orphan-repair banner dismissed state ────────────
  // useCanvas captures dagResult.repairs into canvas.orphanRepairReport
  // on every loadCanvasData. V3 surfaces this in a dismissible banner;
  // V4 was silently swallowing it. We mirror the V3 surface but track
  // dismissal in local state so the banner clears on click without
  // mutating useCanvas's report (which is the source of truth for the
  // last load's repair set). On the next loadCanvasData, useCanvas
  // clears its own report and the banner re-shows iff repairs again.
  const [orphanRepairDismissed, setOrphanRepairDismissed] = useState(false);
  const [orphanRepairExpanded, setOrphanRepairExpanded] = useState(false);
  // Reset dismissal whenever a new repair report lands.
  useEffect(() => {
    if (canvas.orphanRepairReport) {
      setOrphanRepairDismissed(false);
      setOrphanRepairExpanded(false);
    }
  }, [canvas.orphanRepairReport]);

  // Phase 4a — dirty-state indicator. True between any mutation and the
  // next successful Save (or fresh load). Drives the small orange dot on
  // the Save button so the user can tell at a glance whether their
  // changes are persisted. Reset by handleSave on success and by the
  // load useEffect on workflow load.
  const [dirty, setDirty] = useState(false);

  // Hoisted ABOVE the Phase 3a snapshot-capture useEffect so its deps
  // array can reference `payload` without a temporal-dead-zone error.
  // (`const payload = {...}` is otherwise declared right before the JSX
  // return — but that's too late for hooks that use it as a dep.)
  const payload = {
    nodes: canvas.nodes || [],
    connections: canvas.connections || [],
  };

  // ── Phase 5e — Validation pass over the live canvas payload ───────────
  // Pure analyzer (no React, no DOM, no network). Runs on every payload
  // change. Returns { issues: [...], stats: {error, warning, info, total} }
  // — see frontend/src/pages/v4/v4Validate.js for the full rule list.
  const validation = useMemo(() => {
    const p = { nodes: canvas.nodes || [], connections: canvas.connections || [] };
    return validateWorkflow(p);
  }, [canvas.nodes, canvas.connections]);

  // Map sourceNodeId → highest-severity for fast V4CanvasView decoration.
  // Severity ranking: error > warning > info. A node with one error AND
  // two warnings paints as error (the highest wins).
  const validationByNodeId = useMemo(() => {
    const rank = { error: 3, warning: 2, info: 1 };
    const m = new Map();
    (validation.issues || []).forEach((i) => {
      if (!i.nodeId) return;
      const existing = m.get(i.nodeId);
      if (!existing || rank[i.severity] > rank[existing]) {
        m.set(i.nodeId, i.severity);
      }
    });
    return m;
  }, [validation.issues]);

  // ── Phase 4c — Detect V4-unsupported features in the loaded workflow ──
  // V4 ships with the common-case editing surface. Some V3 features (the
  // Cognitive Brief slideout, external-agent vendor config, the runbook
  // agent banner, the JSON Code tab, AutoLet CEG read-only locks) are not
  // in V4's scope and may never be — they're long-tail edit modes best
  // owned by V3. When the loaded workflow uses one of these surfaces, we
  // surface a clear banner telling the operator they should open in V3
  // for full editing, and exactly which feature is missing.
  const v4Limitations = useMemo(() => {
    const out = [];
    const nodes = canvas.nodes || [];
    // External agents (ExteLet family)
    if (nodes.some((n) => n?.isExternal === true
        || n?.agentClassification === 'EXTERNAL_API'
        || n?.agentClassification === 'EXTERNAL_SDK'
        || n?.agentClassification === 'HYBRID')) {
      out.push({
        id: 'external',
        label: 'External agents',
        why: 'V4 renders external agents but does not yet ship the vendor verification + compatibility editor. Open in V3 to edit registration metadata, vendor contact, and API version compatibility.',
      });
    }
    // Cognitive Brief — Phase 7h shipped the slideout (double-click a
    // cognitive_loop node to open). No banner needed anymore. Kept
    // the comment so the next reader sees the surface is intentional,
    // not forgotten.
    // Linked-agent workflow — V3 shows a runbook context banner
    if (workflowMeta?.agentId) {
      out.push({
        id: 'runbook',
        label: 'Linked-agent runbook context',
        why: 'This workflow is linked to an agent. V3 surfaces a runbook context banner above the canvas explaining the agent provenance — V4 does not yet ship that surface.',
      });
    }
    // Protected / locked status — AutoLet CEG read-only enforcement
    if (workflowMeta?.status && /protected|locked/i.test(workflowMeta.status)) {
      out.push({
        id: 'locked',
        label: 'AutoLet CEG read-only enforcement',
        why: 'This workflow is in a locked state (status carries protection/lock semantics). V3 applies fine-grained AutoLet CEG read-only enforcement and surfaces the lock reason — V4 currently treats it as a regular workflow.',
      });
    }
    return out;
  }, [canvas.nodes, workflowMeta]);

  // Phase 1 — load workflow on mount/param change. Uses the V3-proven
  // canvas.loadCanvasData path so the schema is identical and any
  // workflow that loads in V3 also loads in V4.
  useEffect(() => {
    if (!workflowId) {
      setWorkflowMeta(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    api.getWorkflow(workflowId)
      .then((res) => {
        if (cancelled) return;
        // apiFetch envelope: { ok, data, etag } — unwrap. Fallback to
        // `res` itself so this works against any future endpoint that
        // returns the workflow directly without the envelope.
        const wf = (res && typeof res === 'object' && 'data' in res) ? res.data : res;
        if (!wf || typeof wf !== 'object') {
          addToast('Workflow not found', 'error');
          return;
        }
        setWorkflowMeta({ id: wf.id, name: wf.name, status: wf.status });
        const def = wf.definition || wf.dag || { nodes: [], connections: [] };
        if (typeof canvas.loadCanvasData === 'function') {
          canvas.loadCanvasData(def, { autoLayoutOnLoad: true, zoomToFitOnLoad: true });
        }
        // Phase 4a — fresh load = pristine; clear any dirty flag that
        // might be lingering from a prior workflow on the same mount.
        setDirty(false);
        // Phase 4b — beacon for first impression metrics.
        trackTelemetry('v4_workflow_loaded', {
          node_count: def?.nodes?.length || 0,
          conn_count: def?.connections?.length || 0,
        }, { workflowId });
      })
      .catch((e) => {
        if (cancelled) return;
        addToast(`Failed to load workflow: ${e?.message || String(e)}`, 'error');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // Deps intentionally limited to [workflowId]: canvas + addToast are
    // stable across renders in practice (PlatformContext + ToastContext
    // hooks return memoized references), and including them would re-fire
    // the fetch on unrelated context churn (e.g. toast appears). Same
    // pattern as OverviewPageV2.jsx auto-load.
  }, [workflowId]);

  // handleNodeMove was REMOVED 2026-05-29 (was Round-1 Patch A —
  // persisted node drags into the BE). ReactFlow node-drag itself was
  // disabled in V4CanvasView at the same time (nodesDraggable: false).
  // Operator quote: "this feature NEVER worked properly". ELK owns
  // layout exclusively; the Re-layout button is the only user-
  // controlled re-position path. The `manualPosition` flag set by
  // this handler is now never written, so any future loader that
  // reads it can safely fall through to ELK defaults.

  // ── Patch B native: manual edge create with rejection toast ──
  const handleConnect = useCallback(({ sourceNodeId, targetNodeId, sourceHandle, targetHandle }) => {
    if (!sourceNodeId || !targetNodeId) return;
    if (typeof canvas.addConnection !== 'function') return;
    const id = canvas.addConnection(
      sourceNodeId, sourceHandle || 'default',
      targetNodeId, targetHandle || 'default',
    );
    if (id == null) {
      addToast('Connection not allowed (cycle, type mismatch, or duplicate)', 'warning');
      trackTelemetry('v4_edge_rejected', { reason: 'canConnect_returned_null' }, { workflowId });
    } else {
      trackTelemetry('v4_edge_drawn', { connectionId: id }, { workflowId });
    }
  }, [canvas, addToast, workflowId]);

  const handleSelectNode = useCallback((sourceNodeId) => {
    if (sourceNodeId == null) {
      if (typeof canvas.clearSelection === 'function') canvas.clearSelection();
      return;
    }
    if (typeof canvas.selectNode === 'function') canvas.selectNode(sourceNodeId);
  }, [canvas]);

  // Phase 7h — node double-click router + Cognitive Brief save handler
  // are defined LATER in this file, right after the
  // briefSlideoutOpen / linkedAgent / briefSaving state declarations
  // (see ~line 1200). Both useCallbacks list those state pieces in
  // their dep arrays — defining them here would TDZ-throw at render
  // ("Cannot access 'linkedAgent' before initialization") since the
  // state hooks haven't been declared yet at this point in the
  // function body. Don't move them back up without moving the state
  // up too.

  // ── Phase 2a + 2b — TaskPicker insertion flow ────────────────────
  // Polymorphic: accepts either a string sourceNodeId (per-leaf "+", from
  // V2Leaf — Phase 2a) or a target object (per-branch "+", from
  // V2CaseContainer / V2LegContainer — Phase 2b).
  //
  // For branch targets, we resolve the canvas connection that represents
  // the head→branch edge (`from = ownerNodeId, fromPort = branchName`) and
  // hand the picker a `{kind:'branch', branchEdgeId}` target. The primitive
  // (canvas.addTaskToFlow mode 3) splices on that edge, so the new task
  // becomes the first body node of an empty branch, or the new "first
  // step" when the branch has children downstream.
  const handleAddAfter = useCallback((arg) => {
    if (!arg) return;
    // Leaf case — bare string
    if (typeof arg === 'string') {
      setInsertionTarget({ kind: 'after-node', nodeId: arg });
      trackTelemetry('picker_open', { source: 'leaf_plus', target_kind: 'after-node' }, { workflowId });
      return;
    }
    // Branch case — discriminated-union target object
    if (arg && arg.kind === 'branch') {
      const ownerId = arg.ownerNodeId;
      const branchName = arg.branchName;
      if (!ownerId) {
        addToast('Cannot resolve branch — owner switch/fork id missing', 'error');
        return;
      }
      // Resolve the branch's head edge: from = switch/fork id, fromPort = branch name.
      // Fall back to any outgoing edge from the owner if the port match fails
      // (defensive — some legacy connections may not carry fromPort).
      const conns = canvas.connections || [];
      let branchConn = conns.find((c) =>
        c.from === ownerId && (c.fromPort === branchName || c.sourcePort === branchName)
      );
      if (!branchConn && branchName) {
        // Last resort: any edge from owner labeled with this branch
        branchConn = conns.find((c) => c.from === ownerId && (c.label === branchName || c.branchName === branchName));
      }
      if (!branchConn) {
        // Phase 5f-2 — diagnostic for the rare case where ALL fallback
        // matchers fail. V4 already tries fromPort / sourcePort / label /
        // branchName in priority order (above) — this final degradation
        // means the connection set genuinely doesn't carry any of those
        // hints for the chosen branch. Log so future investigations can
        // see the actual edge shape that defeated the lookup.
        // eslint-disable-next-line no-console
        console.warn('[V4 branch+] Lookup failed for branch:', branchName,
          'owner:', ownerId,
          'outgoing edges:', (canvas.connections || []).filter((c) => c.from === ownerId));
        addToast(
          `Branch "${branchName}" has no head edge yet — open in V3 to add the first node, then return to V4.`,
          'warning'
        );
        return;
      }
      setInsertionTarget({
        kind: 'branch',
        branchEdgeId: branchConn.id,
        // Carry through context for picker display + telemetry
        switchNodeId: ownerId,
        branchLabel: branchName,
      });
      trackTelemetry('picker_open', { source: 'branch_plus', target_kind: 'branch', branch_label: branchName }, { workflowId });
      return;
    }
    // Unknown shape — log so we catch future contract drift early
    // eslint-disable-next-line no-console
    console.warn('[CanvasPageV4] handleAddAfter received unknown arg shape', arg);
  }, [canvas, addToast]);

  // Picker selection → route to canvas.addTaskToFlow. The primitive owns
  // the splice/append decision (see useCanvas.js:1335-1424). We just
  // enforce the multi-entry invariant up here so the toast is precise.
  const handlePickerChoose = useCallback((item) => {
    const target = insertionTarget;
    if (!target) return;
    const picked = typeof item === 'string'
      ? { kind: 'type', type: item }
      : (item || {});

    // Phase 2a only handles direct type insertion. Recipe insertion
    // (multi-node splice) and palette mode land in Phase 2b alongside
    // the per-branch + work, since recipes need edge-target resolution.
    if (!picked.type) {
      setInsertionTarget(null);
      return;
    }

    if (SINGLE_INSTANCE_ENTRY_TYPES.has(picked.type)) {
      const existing = (canvas.nodes || []).find((n) =>
        SINGLE_INSTANCE_ENTRY_TYPES.has(n.type));
      if (existing) {
        addToast(
          `This workflow already has an entry node ("${existing.label || existing.type}"). `
          + 'A workflow can have only one Start. Delete it first if you want to replace it.',
          'warning'
        );
        setInsertionTarget(null);
        return;
      }
    }

    const nodeDef = getNodeTypeDef(picked.type);
    if (!nodeDef) {
      addToast('Unknown node type', 'error');
      setInsertionTarget(null);
      return;
    }

    const anchor = (typeof target === 'string')
      ? { kind: 'edge', connectionId: target }
      : target;
    const result = canvas.addTaskToFlow(
      picked.type,
      picked.label || nodeDef.label,
      picked.config || {},
      anchor,
      null,
    );
    if (result?.id) {
      addToast(result.toast || `Inserted ${picked.label || nodeDef.label}`, 'success');
      trackTelemetry('row_chosen', { kind: picked.kind || 'type', type: picked.type, target_kind: anchor?.kind, mode: result.mode }, { workflowId });
    } else {
      addToast('Could not insert task — anchor may have moved. Try again.', 'error');
      trackTelemetry('row_chosen_failed', { type: picked.type, target_kind: anchor?.kind }, { workflowId });
    }
    setInsertionTarget(null);
  }, [insertionTarget, canvas, addToast, workflowId]);

  // ── Phase 3a — Undo/redo snapshot capture ─────────────────────────
  // Debounced effect: when the canvas payload changes, capture a snapshot
  // 300ms later (if no further change happens in that window). This means
  // a drag-flurry produces a SINGLE history entry once the user stops
  // moving, not one per frame. The suppressUntil guard skips capture
  // immediately after an undo/redo replay so the restored state doesn't
  // re-enter the history as a brand-new mutation.
  useEffect(() => {
    if (!workflowId) return undefined;
    const h = historyRef.current;
    if (Date.now() < h.suppressUntil) return undefined;
    const timer = setTimeout(() => {
      if (Date.now() < h.suppressUntil) return;
      if (typeof canvas.getCanvasData !== 'function') return;
      let snapshot;
      try { snapshot = canvas.getCanvasData(); } catch (_) { return; }
      const snapStr = JSON.stringify(snapshot);
      if (h.lastCaptured && JSON.stringify(h.lastCaptured) === snapStr) return;
      // Push the PREVIOUS snapshot into past (so undo restores it). The
      // current snapshot is held in lastCaptured; we don't need to push
      // it into past until the NEXT mutation displaces it.
      if (h.lastCaptured) {
        h.past.push(h.lastCaptured);
        if (h.past.length > 50) h.past.shift();
      }
      h.lastCaptured = snapshot;
      h.future = []; // any new mutation invalidates the redo stack
      setUndoTick((t) => t + 1);
      // Phase 4a — every real capture means an unsaved mutation happened.
      // Set dirty=true unless this was the INITIAL load capture (no prior
      // lastCaptured) — that's pristine state, not a user edit. We use
      // h.past.length > 0 as the post-load signal because past gets its
      // first entry only after the first real mutation.
      if (h.past.length > 0) setDirty(true);
    }, 300);
    return () => clearTimeout(timer);
  }, [payload, workflowId, canvas]);

  const handleUndo = useCallback(() => {
    const h = historyRef.current;
    if (h.past.length === 0) return;
    const restored = h.past.pop();
    if (h.lastCaptured) h.future.unshift(h.lastCaptured);
    h.suppressUntil = Date.now() + 700;
    if (typeof canvas.loadCanvasData === 'function') {
      canvas.loadCanvasData(restored, { autoLayoutOnLoad: false, zoomToFitOnLoad: false });
    }
    h.lastCaptured = restored;
    setUndoTick((t) => t + 1);
    addToast('Undone', 'success');
    trackTelemetry('v4_undo', { remaining_past: h.past.length, future_depth: h.future.length }, { workflowId });
  }, [canvas, addToast, workflowId]);

  const handleRedo = useCallback(() => {
    const h = historyRef.current;
    if (h.future.length === 0) return;
    const restored = h.future.shift();
    if (h.lastCaptured) h.past.push(h.lastCaptured);
    h.suppressUntil = Date.now() + 700;
    if (typeof canvas.loadCanvasData === 'function') {
      canvas.loadCanvasData(restored, { autoLayoutOnLoad: false, zoomToFitOnLoad: false });
    }
    h.lastCaptured = restored;
    setUndoTick((t) => t + 1);
    addToast('Redone', 'success');
    trackTelemetry('v4_redo', { past_depth: h.past.length, remaining_future: h.future.length }, { workflowId });
  }, [canvas, addToast, workflowId]);

  // ── Phase 3b — Copy/paste single node ─────────────────────────────
  const handleCopy = useCallback(() => {
    const selId = canvas.selected;
    if (!selId) return;
    const node = (canvas.nodes || []).find((n) => n.id === selId);
    if (!node) return;
    if (SINGLE_INSTANCE_ENTRY_TYPES.has(node.type) || node.type === 'end' || node.type === 'terminate') {
      addToast('Cannot copy sentinel nodes (Start/End/Terminate)', 'warning');
      return;
    }
    setClipboard({
      type: node.type,
      label: node.label,
      // Deep-clone the config so future edits to the original don't bleed
      // into the pasted copy.
      config: JSON.parse(JSON.stringify(node.config || {})),
    });
    addToast(`Copied ${node.label || node.type}`, 'success');
    trackTelemetry('v4_copy', { type: node.type }, { workflowId });
  }, [canvas, addToast, workflowId]);

  const handlePaste = useCallback(() => {
    if (!clipboard || typeof canvas.addNode !== 'function') return;
    // Defense in depth — clipboard MIGHT contain a sentinel type if the
    // user copied something legitimate earlier and the type was later
    // banned. Refuse here too.
    if (SINGLE_INSTANCE_ENTRY_TYPES.has(clipboard.type)) {
      const existing = (canvas.nodes || []).find((n) => SINGLE_INSTANCE_ENTRY_TYPES.has(n.type));
      if (existing) {
        addToast('Cannot paste — workflow already has a Start', 'warning');
        return;
      }
    }
    // Place the paste 40px down-right of the source if known, else default
    // to (200, 200) — same default as canvas.addNode for orphan drops.
    const ref = (canvas.nodes || []).find((n) => n.id === canvas.selected);
    const x = (ref?.x ?? 200) + 40;
    const y = (ref?.y ?? 200) + 40;
    const id = canvas.addNode(clipboard.type, x, y, clipboard.config, `${clipboard.label} copy`);
    if (id) {
      addToast(
        `Pasted ${clipboard.label} (disconnected — drag from another node to connect it)`,
        'success'
      );
      trackTelemetry('v4_paste', { type: clipboard.type }, { workflowId });
    }
  }, [clipboard, canvas, addToast, workflowId]);

  // ── Phase 2a — keyboard delete on the selected node ──────────────
  const handleDeleteSelected = useCallback(() => {
    const selId = canvas.selected;
    if (!selId || typeof canvas.deleteNode !== 'function') return;
    // Refuse to delete sentinels — V3's flatToIR crashes on a Start-less
    // graph. The user can always re-add Start via the picker.
    const node = (canvas.nodes || []).find((n) => n.id === selId);
    if (node && (node.type === 'manual_start' || node.type === 'start' || node.type === 'end' || node.type === 'terminate')) {
      addToast(
        `Cannot delete ${node.type === 'end' || node.type === 'terminate' ? 'End' : 'Start'} sentinel — every workflow needs one.`,
        'warning'
      );
      return;
    }
    canvas.deleteNode(selId);
    addToast(`Deleted ${node?.label || 'node'}`, 'success');
    trackTelemetry('v4_delete', { type: node?.type }, { workflowId });
  }, [canvas, addToast, workflowId]);

  // ── Phase 4a — multi-delete (shift+click or box-select then Delete) ──
  // Receives canvas-shape sourceNodeIds, filters out sentinels (with a
  // single combined toast — avoids a wall of N warnings if the user
  // happened to multi-select Start/End along with task nodes), then
  // deletes the rest. Reports the success count.
  const handleDeleteMany = useCallback((sourceIds) => {
    if (!Array.isArray(sourceIds) || sourceIds.length === 0) return;
    if (typeof canvas.deleteNode !== 'function') return;
    const SENTINEL_TYPES = new Set(['manual_start', 'start', 'end', 'terminate']);
    const nodeMap = new Map((canvas.nodes || []).map((n) => [n.id, n]));
    let sentinelsSkipped = 0;
    let deleted = 0;
    for (const id of sourceIds) {
      const node = nodeMap.get(id);
      if (!node) continue;
      if (SENTINEL_TYPES.has(node.type)) { sentinelsSkipped++; continue; }
      canvas.deleteNode(id);
      deleted++;
    }
    if (deleted === 0 && sentinelsSkipped > 0) {
      addToast('Cannot delete sentinel nodes — every workflow needs a Start and End.', 'warning');
      return;
    }
    let msg = `Deleted ${deleted} node${deleted === 1 ? '' : 's'}`;
    if (sentinelsSkipped > 0) msg += ` (skipped ${sentinelsSkipped} sentinel${sentinelsSkipped === 1 ? '' : 's'})`;
    addToast(msg, 'success');
    trackTelemetry('v4_delete_many', { count: deleted, sentinels_skipped: sentinelsSkipped }, { workflowId });
  }, [canvas, addToast, workflowId]);

  // ── Phase 5e — jump-to-node from the validation panel ────────────
  // Selecting the node triggers the existing decoration ring + opens
  // the Inspector on it. ReactFlow doesn't expose a programmatic scroll
  // from the host shell without ReactFlowProvider context — for now we
  // settle for selection; future enhancement is a setCenter() call from
  // inside V4CanvasView once we expose the rfNode → screen-position map.
  const handleJumpToNode = useCallback((sourceNodeId) => {
    if (!sourceNodeId) return;
    if (typeof canvas.selectNode === 'function') {
      canvas.selectNode(sourceNodeId);
    }
    trackTelemetry('v4_validation_jump', { source_node_id: sourceNodeId }, { workflowId });
  }, [canvas, workflowId]);

  // ── Phase 5b — palette insertion handlers ─────────────────────────
  // Click-to-add (orphan placement) and drag-drop both funnel through
  // here. The drag-drop path passes the cursor's flow-space coords; the
  // click path uses a default offset from the canvas center. Both honor
  // the SINGLE_INSTANCE_ENTRY_TYPES invariant + getNodeTypeDef lookup.

  // Refuses sentinel + entry-type insertion. Returns true on accept, false
  // on refusal (caller surfaces the toast).
  const guardPaletteInsert = useCallback((type) => {
    if (!type) return false;
    if (SINGLE_INSTANCE_ENTRY_TYPES.has(type)) {
      const existing = (canvas.nodes || []).find((n) =>
        SINGLE_INSTANCE_ENTRY_TYPES.has(n.type));
      if (existing) {
        addToast(
          `This workflow already has an entry node ("${existing.label || existing.type}"). `
          + 'A workflow can have only one Start.',
          'warning'
        );
        return false;
      }
    }
    const def = getNodeTypeDef(type);
    if (!def) {
      addToast(`Unknown node type "${type}"`, 'error');
      return false;
    }
    // PARITY-V3: only manual_start / start are singleton-gated above.
    // Other trigger types (webhook, schedule, siem_alert, email_trigger,
    // system_event_trigger) get NORMAL handling — operators can freely
    // add multiple, alongside any existing Start. The earlier
    // BUG-V4PAL-002 block was overzealous and removed per user request:
    //   "WE HAD ADDED NORMAL HANDLING EXCEPT FOR MANUAL START AND STOP
    //    NODES in V3. USER SHOULD BE ABLE TO ADD TRIGGERS OTHER THAN
    //    MANUAL START AND END NODE."
    // handlePaletteAdd routes triggers to free placement (tier 3) so
    // they don't get spliced mid-spine where their empty in-port would
    // cause an unrunnable edge.
    return true;
  }, [canvas, addToast]);

  // Phase 5f-3 — G397 anchor cascade (ported from V3).
  //
  // Click-to-add chooses an anchor in this priority order:
  //
  //   1. AFTER SELECTED — if exactly one node is selected AND it's not a
  //      sentinel, splice the new node after it (on the selected node's
  //      outgoing edge). The operator's mental model is "I clicked here,
  //      add it next to where I'm working."
  //   2. BEFORE END — no selection but the workflow has an End sentinel
  //      with at least one incoming edge → splice on that edge so the
  //      new node lands wired-into the flow ahead of End.
  //   3. FREE PLACEMENT — no anchor possible (empty workflow / no End yet
  //      / selected node is a sentinel) → raw addNode with stagger.
  //
  // Using canvas.addTaskToFlow for the first two cases (it owns the splice
  // semantics) and raw canvas.addNode only as the last-resort fallback.
  const handlePaletteAdd = useCallback((type) => {
    if (!guardPaletteInsert(type)) return;
    const def = getNodeTypeDef(type);
    const SENTINEL_TYPES = new Set(['manual_start', 'start', 'end', 'terminate']);

    // PARITY-V3: triggers (nodes with no input port) get NORMAL handling
    // — they can be added even when a Start sentinel is present — but
    // they MUST NOT be spliced mid-spine because an upstream→trigger
    // edge violates the trigger's empty in-port contract and makes the
    // workflow unrunnable. Skip cascade tiers 1+2 (which splice on
    // existing edges) and go straight to free placement so the trigger
    // lands as a standalone node the operator can wire manually.
    const isTriggerType = def && def.category === 'triggers'
      && Array.isArray(def.ports?.in) && def.ports.in.length === 0;
    if (isTriggerType && typeof canvas.addNode === 'function') {
      const count = (canvas.nodes || []).length;
      const x = 240 + (count % 5) * 40;
      const y = 200 + (count % 5) * 40;
      const id = canvas.addNode(type, x, y, {}, def.label || type);
      if (id) {
        addToast(`Added ${def.label || type} — drag from it to wire downstream tasks.`, 'success');
        trackTelemetry('v4_palette_add', { source: 'click', type, anchor: 'trigger-free' }, { workflowId });
        if (typeof canvas.selectNode === 'function') canvas.selectNode(id);
      } else {
        addToast(`Could not add ${def.label || type}`, 'error');
      }
      return;
    }

    // Cascade tier 1: after the currently-selected node.
    const selId = canvas.selected;
    const selNode = selId ? (canvas.nodes || []).find((n) => n.id === selId) : null;
    const selectedIsAnchorable = selNode && !SENTINEL_TYPES.has(selNode.type);

    if (selectedIsAnchorable && typeof canvas.addTaskToFlow === 'function') {
      const result = canvas.addTaskToFlow(
        type, def?.label || type, {}, { kind: 'after-node', nodeId: selId }, null
      );
      if (result?.id) {
        addToast(result.toast || `Inserted ${def?.label || type} after selection`, 'success');
        trackTelemetry('v4_palette_add', { source: 'click', type, anchor: 'after-selected', mode: result.mode }, { workflowId });
        if (typeof canvas.selectNode === 'function') canvas.selectNode(result.id);
        return;
      }
      // Fall through to next tier if addTaskToFlow returned no id.
    }

    // Cascade tier 2: before the End sentinel.
    const endNode = (canvas.nodes || []).find((n) => n.type === 'end' || n.type === 'terminate');
    if (endNode && typeof canvas.addTaskToFlow === 'function') {
      const incomingToEnd = (canvas.connections || []).find((c) => c.to === endNode.id);
      if (incomingToEnd) {
        const result = canvas.addTaskToFlow(
          type, def?.label || type, {}, { kind: 'edge', connectionId: incomingToEnd.id }, null
        );
        if (result?.id) {
          addToast(result.toast || `Inserted ${def?.label || type} before End`, 'success');
          trackTelemetry('v4_palette_add', { source: 'click', type, anchor: 'before-end', mode: result.mode }, { workflowId });
          if (typeof canvas.selectNode === 'function') canvas.selectNode(result.id);
          return;
        }
      }
    }

    // Cascade tier 3: ask canvas.addTaskToFlow with NO anchor — it has
    // its own decision tree (decision #4 "append before End", #5
    // "append after Start", #6 free) that handles edge cases the host
    // can't see. Crucially this AVOIDS the old "raw addNode → orphan"
    // path that operators kept hitting (validation panel filled with
    // ORPHAN_NODE warnings). If addTaskToFlow can also wire it (e.g.
    // empty canvas with Start), it does. If truly impossible (workflow
    // somehow without sentinels), it falls back to free addNode, but
    // surfaces a louder "disconnected" toast so the operator knows.
    if (typeof canvas.addTaskToFlow === 'function') {
      const result = canvas.addTaskToFlow(type, def?.label || type, {}, null, null);
      if (result?.id) {
        addToast(result.toast || `Inserted ${def?.label || type}`, 'success');
        trackTelemetry('v4_palette_add', { source: 'click', type, anchor: 'auto', mode: result.mode }, { workflowId });
        if (typeof canvas.selectNode === 'function') canvas.selectNode(result.id);
        return;
      }
    }
    // Last-resort fallback — workflow has no sentinels at all. Add as
    // free node but be loud about it so the operator doesn't end up
    // with a silent orphan.
    if (typeof canvas.addNode !== 'function') return;
    const count = (canvas.nodes || []).length;
    const x = 240 + (count % 5) * 40;
    const y = 200 + (count % 5) * 40;
    const id = canvas.addNode(type, x, y, {}, def?.label || type);
    if (id) {
      addToast(
        `Added ${def?.label || type} but couldn't wire it — drag from another node to connect.`,
        'warning'
      );
      trackTelemetry('v4_palette_add', { source: 'click', type, anchor: 'free-fallback' }, { workflowId });
      if (typeof canvas.selectNode === 'function') canvas.selectNode(id);
    } else {
      addToast(`Could not add ${def?.label || type}`, 'error');
      trackTelemetry('v4_palette_add', { source: 'click', type, failed: true }, { workflowId });
    }
  }, [canvas, addToast, guardPaletteInsert, workflowId]);

  // (handlePaletteDrop was REMOVED 2026-05-29. The whole drag-drop
  // path created orphan nodes by free-placing at the cursor coords
  // instead of routing through the smart cascade. Add paths now: (1)
  // palette click → handlePaletteAdd above, (2) per-node "+" →
  // TaskPicker, (3) ⌘K command palette → also cascade. See
  // V4CanvasView for the architectural rationale. Don't add a new
  // drop handler without a written smart-routing strategy.)

  // ── Phase 5c-1 — switch to another workflow ──────────────────────
  // Honors the dirty-state save-first prompt so unsaved edits aren't
  // silently discarded when the operator clicks a sibling workflow in
  // the dropdown. Uses react-router's navigate so the URL change is a
  // proper SPA route transition (the load useEffect picks up the new
  // workflowId via searchParams).
  const handleSwitchWorkflow = useCallback((nextId) => {
    if (!nextId || nextId === workflowId) return;
    const goTo = () => navigate(`/canvas-v4?workflowId=${encodeURIComponent(nextId)}`);
    if (dirty && workflowId) {
      const choice = window.confirm(
        'You have unsaved changes in the current workflow.\n\n'
        + 'OK = Save first, then switch\n'
        + 'Cancel = Discard changes and switch anyway'
      );
      trackTelemetry('v4_switch_workflow', { dirty: true, user_choice: choice ? 'save_first' : 'discard' }, { workflowId });
      if (choice) {
        handleSaveRef.current?.().then(() => goTo()).catch(() => goTo());
      } else {
        goTo();
      }
      return;
    }
    trackTelemetry('v4_switch_workflow', { dirty: false }, { workflowId });
    goTo();
  }, [workflowId, dirty, navigate]);

  // handleSave is defined below; we hold a ref so handleSwitchWorkflow
  // (above) can call it without forcing it into our dep array. Wired by
  // the ref-update useEffect right after handleSave is defined.
  const handleSaveRef = useRef(null);

  // ── Phase 5c-2/3 — open create or save-as dialog ─────────────────
  const openNewDialog = useCallback(() => {
    setCreateDialog({ mode: 'new', defaultName: '' });
    trackTelemetry('v4_new_workflow_open', {}, { workflowId });
  }, [workflowId]);

  const openSaveAsDialog = useCallback(() => {
    if (!workflowId) return;
    const suggestion = workflowMeta?.name ? `${workflowMeta.name} copy` : 'Untitled copy';
    setCreateDialog({ mode: 'save-as', defaultName: suggestion });
    trackTelemetry('v4_save_as_open', {}, { workflowId });
  }, [workflowId, workflowMeta]);

  // ── Phase 5c-2/3 — confirm: actually POST createWorkflow ─────────
  // For 'new': seed payload with Start → End sentinel (mirrors V3
  // makeFreshSentinelPayload). For 'save-as': clone current canvas.
  const handleCreateConfirm = useCallback(async ({ name, description }) => {
    if (!createDialog) return;
    const mode = createDialog.mode;
    setCreateBusy(true);
    try {
      let definition;
      if (mode === 'save-as') {
        if (typeof canvas.getCanvasData === 'function') {
          definition = canvas.getCanvasData();
        } else {
          definition = { nodes: canvas.nodes || [], connections: canvas.connections || [] };
        }
      } else {
        // mode === 'new' — seed Start → End sentinel (same shape as V3
        // makeFreshSentinelPayload). IDs are deterministic-by-timestamp so
        // every fresh canvas gets a unique pair.
        const t = Date.now();
        const startId = `n_${t}_start`;
        const endId = `n_${t}_end`;
        definition = {
          nodes: [
            { id: startId, type: 'manual_start', x: 400, y: 60, config: {}, label: 'Start' },
            { id: endId,   type: 'end',          x: 400, y: 300, config: {}, label: 'End' },
          ],
          connections: [
            { id: `c_${t}`, from: startId, to: endId, fromPort: 'out', toPort: 'in' },
          ],
        };
      }
      // CTX-AUTH — include org/tenant/createdBy so the BE INSERT satisfies
      // the workflows.org_id NOT NULL constraint and the row is correctly
      // scoped. Mirrors V3 CanvasPageV3.jsx:2638-2647.
      const payload = {
        name,
        description,
        orgId,
        tenantId,
        status: 'draft',
        createdBy: userId,
        definition,
      };
      const res = await api.createWorkflow(payload);
      const env = (res && typeof res === 'object' && 'data' in res) ? res.data : res;
      const newId = env?.id;
      if (!newId) {
        addToast(`Could not create workflow (${(res && res.error) || 'no id returned'})`, 'error');
        trackTelemetry('v4_create_workflow', { mode, success: false }, { workflowId });
        return;
      }
      addToast(
        mode === 'save-as' ? `Cloned to "${name}"` : `Workflow "${name}" created`,
        'success'
      );
      trackTelemetry('v4_create_workflow', { mode, success: true, new_id: newId }, { workflowId });
      // Close the dialog BEFORE navigating so the dialog unmounts cleanly
      // and the next mount of CanvasPageV4 (post-route-change) starts fresh.
      setCreateDialog(null);
      navigate(`/canvas-v4?workflowId=${encodeURIComponent(newId)}`);
    } catch (e) {
      addToast(`Create failed: ${e?.message || String(e)}`, 'error');
      trackTelemetry('v4_create_workflow', { mode, success: false, error: e?.message?.slice(0, 100) }, { workflowId });
    } finally {
      setCreateBusy(false);
    }
  }, [createDialog, canvas, navigate, addToast, workflowId, orgId, tenantId, userId]);

  // Phase 5b — soft-hide types from the palette when an instance already
  // exists. Right now that's only the entry sentinels (Start). Computed
  // here so the palette and the guard share the same source of truth.
  const paletteHiddenTypes = useMemo(() => {
    const out = [];
    const hasStart = (canvas.nodes || []).some((n) => SINGLE_INSTANCE_ENTRY_TYPES.has(n.type));
    if (hasStart) out.push('start');
    return out;
  }, [canvas.nodes]);

  // Save the current canvas. Manual saves (Save button click, dirty-prompt
  // path) show a success toast; auto-save callers pass {silent: true} so
  // the periodic background save doesn't spam the toast stack — the
  // inline auto-save status (see Phase 6c-2) communicates success
  // instead. Failures always surface as a toast regardless of caller
  // (V3 audit P2 #14: V3 swallows auto-save failures, V4 does not).
  //
  // Phase 7i — phase-allowlist rejection (HTTP 409/423) is special:
  // api.apiFetch returns those as { ok: false, status, ...body } rather
  // than throwing. Manual saves open the governance diff modal; silent
  // auto-saves emit a warning toast (don't pop a modal mid-typing). The
  // modal's Regress button re-fires this same handleSave after demoting
  // the agent's phase. Every other failure mode falls through to the
  // generic error toast.
  const handleSave = useCallback(async (opts = {}) => {
    if (!workflowId || !workflowMeta) return;
    const { silent = false } = opts;
    setSaving(true);
    try {
      const dag = typeof canvas.getCanvasData === 'function'
        ? canvas.getCanvasData()
        : { nodes: canvas.nodes, connections: canvas.connections };
      const res = await api.updateWorkflow(workflowId, {
        ...workflowMeta,
        definition: dag,
      });
      // Phase 7i — phase-allowlist rejection branch. api.apiFetch wraps
      // 409 / 423 into a structured body instead of throwing.
      if (res && res.ok === false && (res.status === 409 || res.status === 423)) {
        if (silent) {
          // Auto-save mid-edit — don't pop a modal. Warn instead; the
          // operator's next Save click will surface the full diff.
          addToast(
            'Auto-save blocked by governance. Click Save to see the diff + regress option.',
            'warning'
          );
        } else {
          // Manual save — open the diff modal. The linkedAgent fetch
          // happens in a separate effect (see Phase 7i agent-load
          // effect below) so handleSave doesn't depend on the
          // linkedAgent state — that state is declared AFTER this
          // useCallback runs at module-load time, which would TDZ.
          setDiffModal({
            error: res.error || res.message || 'Save blocked by phase guard',
            attemptedAdds:    res.attemptedAdds,
            attemptedRemoves: res.attemptedRemoves,
            attemptedMutates: res.attemptedMutates,
            regressTarget:    res.regressTarget || res.targetPhase || null,
            phase:            res.phase || null,
            status:           res.status,
          });
        }
        trackTelemetry('v4_save_blocked', { silent, status: res.status, hasDiff: !!(res.attemptedAdds || res.attemptedRemoves || res.attemptedMutates) }, { workflowId });
        return;
      }
      // Other non-throw failures (e.g. 412 etag, generic ok:false).
      if (res && res.ok === false) {
        addToast(`Save failed: ${res.error || res.message || `HTTP ${res.status || '?'}`}`, 'error');
        trackTelemetry('v4_save', { success: false, silent, status: res.status }, { workflowId });
        return;
      }
      if (!silent) addToast('Workflow saved', 'success');
      // Phase 4a — successful save clears the dirty flag.
      setDirty(false);
      trackTelemetry('v4_save', { success: true, silent, node_count: dag?.nodes?.length, conn_count: dag?.connections?.length }, { workflowId });
    } catch (e) {
      addToast(`Save failed: ${e?.message || String(e)}`, 'error');
      trackTelemetry('v4_save', { success: false, silent, error: e?.message?.slice(0, 100) }, { workflowId });
    } finally {
      setSaving(false);
    }
  }, [workflowId, workflowMeta, canvas, addToast]);

  // Phase 5c-1 — expose handleSave through the ref so the workflow switcher
  // can call it for the dirty-prompt "save first" path. Updated whenever
  // handleSave's identity changes (which it does on relevant dep updates).
  useEffect(() => { handleSaveRef.current = handleSave; }, [handleSave]);

  // ─── Phase 6c-2 — Auto-save ─────────────────────────────────────
  // Debounced save when the workflow goes dirty. Fires 2s after the
  // last edit. Pauses while:
  //   • no workflow loaded
  //   • a load is in flight (workflowMeta missing or `loading`)
  //   • a save is already in flight (`saving`)
  //   • the workflow is read-only (LOCKED / ARCHIVED status)
  //   • the new-workflow dialog is open
  // The status label sits next to the Save button — "Saving…" while
  // the network call is in flight, "Saved Xs ago" otherwise. The
  // initial mount doesn't trigger an auto-save (dirty starts false
  // and we only react to dirty→true).
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const autoSaveTimerRef = useRef(null);

  // ─── Phase 7a-1 — Workflow execution ────────────────────────────
  // V4 didn't have execution until 7a — operators had to bounce to V3
  // just to test a run. This block adds a Run button next to Save.
  //
  // Flow:
  //   1. Validate first — if Phase 5e flags errors, refuse and toast.
  //      Warnings + info are fine to proceed past. (Mirrors V3 G182.)
  //   2. If dirty, auto-save first (silent) so the BE runs the latest
  //      definition the operator is looking at — otherwise they'd be
  //      executing the prior save's stale graph.
  //   3. POST /workflows/{id}/execute. Surface the returned run id in
  //      the success toast.
  //   4. Hold a `lastRunId` for Phase 7a-3 to deep-link the detail panel.
  //
  // Phase 7g — pre-Run input prompt. Workflows that declare required
  // inputs (trigger node `inputParameters`, or external workflow
  // `input_schema` for GEL Protected wrappers) need values BEFORE the
  // run starts; without them the run starts and immediately fails at
  // the first `${workflow.input.<name>}` resolution.
  //
  // Flow: handleRun resolves required inputs → if any are required,
  // sets executeInputPrompt and returns. The modal's Submit calls
  // runWithInputs(values) which performs the actual execution. Cancel
  // clears the prompt without running.
  //
  // executeInputPrompt shape: { params: [{name, required, type?, description?}, ...], values: {<name>: ...} }
  const [executing, setExecuting] = useState(false);
  const [lastRunId, setLastRunId] = useState(null);
  const [executeInputPrompt, setExecuteInputPrompt] = useState(null);
  // Phase 7a-3 — selected run id for the detail panel. Set by the
  // Runs dropdown or by the Run button (auto-opens the panel on a
  // fresh run so the operator can watch progress).
  const [openRunId, setOpenRunId] = useState(null);

  // Phase 7h — Cognitive Brief slideout state. Three pieces:
  //   • briefSlideoutOpen — UI visibility
  //   • linkedAgent       — the agent object the brief lives on. Lazy
  //                          loaded on first open so V4 doesn't pay
  //                          for an /agents/{id} fetch on workflows
  //                          that never touch a cognitive_loop.
  //   • briefSaving       — in-flight PUT flag drives Save button disable
  // The slideout itself owns the form state (V4CognitiveBriefSlideout
  // re-seeds from linkedAgent.cognitiveBrief on every open).
  const [briefSlideoutOpen, setBriefSlideoutOpen] = useState(false);
  const [linkedAgent, setLinkedAgent] = useState(null);
  const [briefSaving, setBriefSaving] = useState(false);

  // Phase 7i — governance diff modal. handleSave (above) writes to
  // diffModal when the BE rejects with 409/423; the modal lets the
  // operator regress the agent's phase and re-fire the save.
  //   diffModal: null when closed; the rejection body when open.
  //   regressing: in-flight POST /agents/{id}/regress-phase.
  //   resaving:   in-flight handleSave re-fire after a successful regress.
  // setDiffModal lives above handleSave only via closure (the dep array
  // is intentionally short to avoid TDZ on this state — Phase 7h-style).
  const [diffModal, setDiffModal] = useState(null);
  const [regressing, setRegressing] = useState(false);
  const [resaving, setResaving] = useState(false);

  // Phase 7h — node double-click router.
  //
  // Today only cognitive_loop nodes have a meaningful double-click
  // action (open the Cognitive Brief slideout). Other types no-op so
  // the operator's single-click selection isn't disturbed. The handler
  // is kept here in the host (not V4CanvasView) so future per-type
  // double-click actions can read host-only state (workflowMeta,
  // permissions) without re-plumbing props.
  //
  // Defined HERE (post-state-declarations) on purpose — the deps array
  // captures linkedAgent / briefSlideoutOpen / briefSaving which are
  // declared just above. Defining the callback earlier in the function
  // body would TDZ-throw at render time on those bindings.
  const handleNodeDoubleClick = useCallback(async (sourceNodeId, canvasType) => {
    if (canvasType !== 'cognitive_loop') return;
    // The brief lives on the AGENT record (V3 parity — see
    // CanvasPageV3.jsx:7653-7845). Need workflowMeta.agentId to know
    // which agent to load. Workflows that don't have a linked agent
    // can't edit a brief inline today — surface a helpful toast so
    // the operator knows where to go next.
    const agentId = workflowMeta?.agentId;
    if (!agentId) {
      addToast(
        'This workflow has a Cognitive Loop but is not linked to an agent. '
        + 'Brief lives on the agent — link the workflow to one first.',
        'warning'
      );
      return;
    }
    // Re-fetch the agent every open so the slideout never seeds from
    // stale state (e.g. someone else edited the brief in another tab).
    // Cheap — a single GET. We open the slideout immediately with
    // `linkedAgent=null` so the operator sees the loading state, then
    // fill it in. If the fetch fails, close + toast (no half-open UI).
    setLinkedAgent(null);
    setBriefSlideoutOpen(true);
    try {
      const res = await api.getAgent(agentId);
      const payload = (res && typeof res === 'object' && 'data' in res) ? res.data : res;
      if (!payload || !payload.id) {
        throw new Error('Agent response missing id');
      }
      setLinkedAgent(payload);
    } catch (e) {
      setBriefSlideoutOpen(false);
      addToast(`Could not load agent for Cognitive Brief: ${e?.message || String(e)}`, 'error');
    }
  }, [workflowMeta, addToast]);

  // Phase 7h — save handler. Sends ONLY the brief field per V3 parity
  // (CanvasPageV3.jsx:2038-2044) so the backend can merge without
  // clobbering other agent fields. On success: re-fetch the agent so
  // the next open shows the just-saved values; close the slideout;
  // toast. The cognitive_loop node's config carries a read-time copy
  // of the brief — that copy refreshes on the next workflow load.
  // Runtime executor reads the brief at exec time (V3 footer copy:
  // "changes apply on the next execution").
  const handleSaveCognitiveBrief = useCallback(async (brief) => {
    if (!linkedAgent?.id) {
      addToast('No agent loaded — refresh and try again.', 'error');
      return;
    }
    setBriefSaving(true);
    try {
      const res = await api.updateAgent(linkedAgent.id, { cognitiveBrief: brief });
      if (res && res.ok === false) {
        throw new Error(res.error || 'Update failed');
      }
      // Refetch to pick up the canonical server value (e.g. trimmed
      // strings, server-side defaults applied to nullable fields).
      try {
        const ref = await api.getAgent(linkedAgent.id);
        const payload = (ref && typeof ref === 'object' && 'data' in ref) ? ref.data : ref;
        if (payload?.id) setLinkedAgent(payload);
      } catch (_) { /* non-fatal — slideout closes anyway */ }
      addToast('Cognitive Brief saved.', 'success');
      trackTelemetry('v4_cognitive_brief_save', { agentId: linkedAgent.id }, { workflowId });
      setBriefSlideoutOpen(false);
    } catch (e) {
      // Surface to the slideout AND toast — operator may want to
      // re-open and try a tweak without re-typing the whole prompt.
      addToast(`Save failed: ${e?.message || String(e)}`, 'error');
      throw e; // re-throw so the slideout's local error state can show it too
    } finally {
      setBriefSaving(false);
    }
  }, [linkedAgent, addToast, workflowId]);

  // Phase 7i — lazy-fetch the linked agent when the diff modal opens.
  // Reuses any already-loaded linkedAgent (Phase 7h may have fetched it
  // for the Cognitive Brief slideout). Failure is non-fatal — the modal
  // still renders the diff, just with a disabled Regress button.
  useEffect(() => {
    if (!diffModal) return undefined;
    const agentId = workflowMeta?.agentId;
    if (!agentId) return undefined;
    if (linkedAgent && linkedAgent.id === agentId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const ag = await api.getAgent(agentId);
        const payload = (ag && typeof ag === 'object' && 'data' in ag) ? ag.data : ag;
        if (!cancelled && payload?.id) setLinkedAgent(payload);
      } catch (_) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [diffModal, workflowMeta, linkedAgent]);

  // Phase 7i — Regress + re-save. Sends POST /agents/{id}/regress-phase
  // with the BE's recommended target (regressTarget). On success, closes
  // the modal, refreshes linkedAgent (so its phase chip is correct),
  // and re-fires handleSave so the operator's pending changes land. If
  // the re-save itself trips the guard again (e.g. requires Build but
  // we regressed to Decide), handleSave re-opens the modal with the
  // fresh diff — the operator clicks Regress again.
  const handleRegressFromDiffModal = useCallback(async (targetPhase) => {
    const agentId = linkedAgent?.id || workflowMeta?.agentId;
    if (!agentId) {
      addToast('No linked agent — cannot regress phase.', 'error');
      return;
    }
    if (!targetPhase) {
      addToast('No regress target reported by the platform.', 'error');
      return;
    }
    setRegressing(true);
    try {
      const res = await api.regressPhase(agentId, targetPhase, userId || null);
      if (res && res.ok === false) {
        throw new Error(res.error || res.message || `Regress failed (HTTP ${res.status || '?'})`);
      }
      // Refresh agent so the phase chip + later opens are accurate.
      try {
        const ag = await api.getAgent(agentId);
        const payload = (ag && 'data' in ag) ? ag.data : ag;
        if (payload?.id) setLinkedAgent(payload);
      } catch (_) { /* non-fatal */ }
      addToast(`Agent regressed to ${String(targetPhase).toUpperCase()}. Re-saving…`, 'success');
      trackTelemetry('v4_phase_regress', { agentId, target: targetPhase }, { workflowId });
      // Close the diff modal AFTER toggling resaving so the re-fired
      // save shows its own spinner if it pops the modal again.
      setDiffModal(null);
      setResaving(true);
      try {
        await handleSaveRef.current?.();
      } finally {
        setResaving(false);
      }
    } catch (e) {
      addToast(`Regress failed: ${e?.message || String(e)}`, 'error');
      trackTelemetry('v4_phase_regress', { agentId, target: targetPhase, failed: true }, { workflowId });
    } finally {
      setRegressing(false);
    }
  }, [linkedAgent, workflowMeta, userId, addToast, workflowId]);

  // Phase 7g — single-source-of-truth executor. Both the "no inputs
  // required" fast path and the modal's Submit path funnel through here
  // so we get one place to emit toasts, telemetry, and run-detail
  // open-side-effects. `inputValues` is null for the no-prompt path,
  // or the trimmed map from V4ExecuteInputModal.
  const runWithInputs = useCallback(async (inputValues) => {
    if (!workflowId || !workflowMeta) return;
    setExecuting(true);
    try {
      // Auto-save dirty before run so we execute what the operator
      // sees, not the prior save.
      if (dirty && typeof handleSaveRef.current === 'function') {
        await handleSaveRef.current({ silent: true });
      }
      const payload = {
        ...(userId ? { userId } : {}),
        ...(inputValues && Object.keys(inputValues).length > 0 ? { input: inputValues } : {}),
      };
      const res = await api.executeWorkflow(workflowId, payload);
      const env = (res && typeof res === 'object' && 'data' in res) ? res.data : res;
      const runId = env?.id || env?.runId;
      const status = env?.status;
      if (status === 'pending_approval') {
        addToast('Human approval required — workflow is held. Check the Approval Center.', 'info');
      } else if (runId) {
        setLastRunId(runId);
        // Auto-open the run detail panel so the operator immediately
        // sees status / progress without an extra click.
        setOpenRunId(runId);
        addToast(`Run started — ${String(runId).slice(0, 8)}…`, 'success');
      } else if (res && res.ok === false) {
        addToast(`Run failed: ${res.error || 'unknown error'}`, 'error');
      } else {
        addToast('Run started', 'success');
      }
      trackTelemetry('v4_run', {
        success: !!runId,
        run_id: runId,
        with_inputs: !!(inputValues && Object.keys(inputValues).length > 0),
      }, { workflowId });
    } catch (e) {
      addToast(`Run failed: ${e?.message || String(e)}`, 'error');
      trackTelemetry('v4_run', { success: false, error: e?.message?.slice(0, 100) }, { workflowId });
    } finally {
      setExecuting(false);
    }
  }, [workflowId, workflowMeta, dirty, userId, addToast]);

  // Phase 7g — local-scan fallback for required inputs. Fast and
  // sufficient for native canvas workflows where the author declared
  // inputParameters on the start/trigger node. Misses external workflow
  // input schemas (GEL Protected wrappers) — those need the backend
  // resolver below. Kept as a safety net in case the BE endpoint is
  // unavailable in dev.
  const collectRequiredInputParamsLocal = useCallback(() => {
    const startTypes = new Set([
      'manual_start', 'manual_trigger', 'start', 'trigger',
      'form_trigger', 'webhook', 'schedule', 'siem_alert',
      'email_trigger', 'system_event_trigger',
    ]);
    const nodes = canvas.nodes || [];
    const startNode = nodes.find((n) => n && startTypes.has(n.type));
    if (!startNode) return [];
    const ip = startNode.config?.inputParameters;
    if (!Array.isArray(ip)) return [];
    return ip
      .filter((s) => typeof s === 'string' && s.trim().length > 0)
      .map((name) => ({ name, required: true, type: 'string' }));
  }, [canvas.nodes]);

  const handleRun = useCallback(async () => {
    if (!workflowId || !workflowMeta) return;
    if (executing) return;
    // Gate 1 — refuse to run a workflow with validation errors.
    const errCount = validation?.stats?.error || 0;
    if (errCount > 0) {
      addToast(
        `Workflow has ${errCount} error${errCount === 1 ? '' : 's'} — fix them before running.`,
        'warning'
      );
      return;
    }
    // Gate 2 — required-input resolution. The BE merges canvas trigger
    // inputParameters AND linked external workflow input_schema, so
    // GEL Protected wrappers prompt for their wrapped workflow's
    // required fields too. Fall back to local scan on endpoint error
    // (dev mode, network blip) so canvas stays usable.
    let required = [];
    try {
      const res = await api.getWorkflowRequiredInputs(workflowId);
      const env = (res && typeof res === 'object' && 'data' in res) ? res.data : res;
      if (Array.isArray(env?.inputs)) required = env.inputs;
    } catch (e) {
      console.warn('[V4 Run] required-inputs lookup failed, falling back to local scan', e);
      required = collectRequiredInputParamsLocal();
    }
    if (required.length > 0) {
      const seed = {};
      required.forEach((p) => {
        // Seed bool as false, others as ''. Avoids undefined → "uncontrolled
        // input" React warnings in the modal.
        const t = (p.type || 'string').toLowerCase();
        seed[p.name] = (t === 'boolean' || t === 'bool') ? false : '';
      });
      setExecuteInputPrompt({ params: required, values: seed });
      return; // runWithInputs fires from the modal's Submit
    }
    return runWithInputs(null);
  }, [workflowId, workflowMeta, executing, validation, addToast, runWithInputs, collectRequiredInputParamsLocal]);

  // ─── Phase 6c-3 — Command palette (⌘K) ──────────────────────────
  // Owned here so the items list can see every action handler in scope
  // (save / undo / redo / re-layout / switch / insert). The palette is
  // a self-contained dialog; we just toggle its open flag.
  const [paletteOpen, setPaletteOpen] = useState(false);
  // Lazy workflow list for the Switch Workflow group. We refetch each
  // time the palette opens so freshly-created workflows show up. Failure
  // is silent (the palette still works without the workflow group).
  const [paletteWorkflows, setPaletteWorkflows] = useState([]);
  useEffect(() => {
    if (!paletteOpen) return;
    let cancelled = false;
    api.getWorkflows({ orgId, tenantId })
      .then((res) => {
        if (cancelled) return;
        const payload = (res && 'data' in res) ? res.data : res;
        const list = Array.isArray(payload)
          ? payload
          : (Array.isArray(payload?.items) ? payload.items : []);
        setPaletteWorkflows(list);
      })
      .catch(() => { if (!cancelled) setPaletteWorkflows([]); });
    return () => { cancelled = true; };
  }, [paletteOpen, orgId, tenantId]);

  // ─── Phase 7b-2 — AI Builder Build mode handler ─────────────────
  // V4AIBuilder calls this when the operator submits a Build prompt.
  // We POST to /canvas/ai/build with the current canvas + installed
  // connectors, then return a {summary, kind:'diff', payload} so the
  // chat renders a Diff bubble with Apply / Discard buttons.
  //
  // The payload.apply is a closure over canvas.loadCanvasData — it's
  // executed when the operator clicks Apply, NOT immediately. This is
  // the key improvement over V3 (which auto-applied + left operators
  // guessing what changed).
  //
  // Returning the result as a chat message (instead of mutating canvas
  // ourselves) keeps the panel surface pure-UI and makes the apply step
  // observable in the conversation history.
  const handleAIBuildSubmit = useCallback(async (promptText) => {
    if (!workflowId) {
      return { summary: 'No workflow loaded — open one first.', kind: 'error' };
    }
    const currentCanvas = (typeof canvas.getCanvasData === 'function')
      ? canvas.getCanvasData()
      : { nodes: canvas.nodes || [], connections: canvas.connections || [] };
    // installedConnectors is List<String> on the BE — passing `{}` deserializes
    // to LinkedHashMap and crashes the cast. Always send an array.
    const res = await api.aiCanvasBuild(promptText, currentCanvas, [] /* installedConnectors */);
    // apiFetch envelope: {ok, data} OR raw data.
    const payload = (res && 'data' in res) ? res.data : res;
    if (res && res.ok === false) {
      throw new Error(payload?.error || res.error || 'AI build failed');
    }
    if (payload?.error) throw new Error(payload.error);
    const newNodes = Array.isArray(payload?.nodes) ? payload.nodes : [];
    const newConns = Array.isArray(payload?.connections) ? payload.connections : [];
    // Compute a coarse diff. We don't try to match by id (the BE may
    // reassign), so "add" = nodes in newCanvas, "drop" = nodes only in
    // current. "keep" = matched by id. This is intentionally an
    // estimate, not a strict diff — the operator sees the canvas after
    // apply for the truth.
    const currentIds = new Set((currentCanvas.nodes || []).map((n) => n.id));
    const newIds = new Set(newNodes.map((n) => n.id));
    const keepCount = [...currentIds].filter((id) => newIds.has(id)).length;
    const addCount  = newNodes.length - keepCount;
    const dropCount = currentIds.size  - keepCount;
    trackTelemetry('v4_ai_build', { add: addCount, drop: dropCount, keep: keepCount }, { workflowId });
    return {
      summary: payload?.summary || `Generated ${newNodes.length} nodes + ${newConns.length} connections.`,
      kind: 'diff',
      payload: {
        addCount,
        dropCount,
        keepCount,
        warnings: payload?.warnings || [],
        apply: async () => {
          if (typeof canvas.loadCanvasData === 'function') {
            await canvas.loadCanvasData(
              { nodes: newNodes, connections: newConns },
              { autoLayoutOnLoad: true, zoomToFitOnLoad: true },
            );
          }
          addToast('AI-generated workflow applied', 'success');
          trackTelemetry('v4_ai_build_applied', { add: addCount, drop: dropCount }, { workflowId });
        },
        discard: () => {
          trackTelemetry('v4_ai_build_discarded', {}, { workflowId });
        },
      },
    };
  }, [workflowId, canvas, addToast]);

  // ─── Phase 7b-3 — AI Builder Debug + Fix-chip handler ───────────
  // Calls /canvas/ai/debug with current canvas + recent execution
  // results, then renders a 'fix-rail' bubble. Each quick-fix chip
  // invokes a SECOND aiCanvasBuild call scoped to that single fix
  // (V3 pattern: handleApplyChipFix sends a multi-paragraph fix
  // instruction wrapping the chip's machine-readable hint).
  //
  // We include the openRunId's executions if it points to a recent
  // run — gives the BE the live failure data without an extra round
  // trip. Falls back to an empty {} if no run is open.
  const handleAIDebugSubmit = useCallback(async (promptText) => {
    if (!workflowId) {
      return { summary: 'No workflow loaded — open one first.', kind: 'error' };
    }
    const currentCanvas = (typeof canvas.getCanvasData === 'function')
      ? canvas.getCanvasData()
      : { nodes: canvas.nodes || [], connections: canvas.connections || [] };
    // Optionally pull execution data for openRunId. Best-effort —
    // if the fetch fails we send {} and the BE still has the prompt
    // + canvas to reason about.
    let executionResults = {};
    if (openRunId) {
      try {
        const r = await api.getNodeExecutions(openRunId);
        const payload = (r && 'data' in r) ? r.data : r;
        const list = Array.isArray(payload) ? payload : (Array.isArray(payload?.items) ? payload.items : []);
        executionResults = list.reduce((acc, n) => {
          const k = n.nodeId || n.canvasNodeId || n.sourceNodeId || n.id;
          if (k) acc[k] = {
            status: n.status, error: n.error,
            startedAt: n.startedAt, completedAt: n.completedAt,
            output: n.output,
          };
          return acc;
        }, {});
      } catch (_) { /* fall through with empty */ }
    }
    // Same as build — installedConnectors must be List<String>, not a map.
    const res = await api.aiCanvasDebug(promptText, currentCanvas, executionResults, [] /* installedConnectors */);
    const payload = (res && 'data' in res) ? res.data : res;
    if (res && res.ok === false) {
      throw new Error(payload?.error || res.error || 'AI debug failed');
    }
    if (payload?.error) throw new Error(payload.error);
    const quickFixes = Array.isArray(payload?.quickFixes) ? payload.quickFixes : [];
    // Normalize fixes to a consistent shape: { id, label, hint? }.
    const fixes = quickFixes.map((f, i) => {
      if (typeof f === 'string') return { id: `fix_${i}`, label: f, hint: f };
      return {
        id: f.id || f.fixId || `fix_${i}`,
        label: f.label || f.title || f.message || `Fix #${i + 1}`,
        hint: f.hint || f.description || f.label || f.message || '',
      };
    });
    const healthLabel = payload?.overallHealth
      ? `Health: ${payload.overallHealth}`
      : null;
    const summary = payload?.summary
      || `Analysis complete. ${fixes.length} quick-fix${fixes.length === 1 ? '' : 'es'} suggested.`;
    trackTelemetry('v4_ai_debug', { fix_count: fixes.length }, { workflowId });
    return {
      summary,
      kind: 'fix-rail',
      payload: {
        health: healthLabel,
        fixes,
        // applyFix — wraps the chip's hint in a fix-instruction prompt
        // and calls aiCanvasBuild. Mirrors V3's handleApplyChipFix at
        // CanvasPageV3.jsx:8467 but renders the diff (vs auto-apply).
        applyFix: async (fix) => {
          const fixPrompt = [
            'Apply the following fix to the workflow on the canvas.',
            '',
            `Fix: ${fix.label}`,
            fix.hint && fix.hint !== fix.label ? `Detail: ${fix.hint}` : null,
            '',
            'IMPORTANT:',
            '- Make the smallest change needed to apply this fix.',
            '- Preserve all other nodes/connections unless the fix specifically requires removing them.',
            '- Do NOT change node IDs unless the fix requires it.',
          ].filter(Boolean).join('\n');
          const r = await api.aiCanvasBuild(fixPrompt, currentCanvas, {});
          const p = (r && 'data' in r) ? r.data : r;
          if (r && r.ok === false) throw new Error(p?.error || r.error || 'Fix apply failed');
          if (p?.error) throw new Error(p.error);
          const newNodes = Array.isArray(p?.nodes) ? p.nodes : [];
          const newConns = Array.isArray(p?.connections) ? p.connections : [];
          if (typeof canvas.loadCanvasData === 'function') {
            await canvas.loadCanvasData(
              { nodes: newNodes, connections: newConns },
              { autoLayoutOnLoad: true, zoomToFitOnLoad: true },
            );
          }
          addToast(`Applied fix: ${fix.label}`, 'success');
          trackTelemetry('v4_ai_fix_applied', { fix_id: fix.id }, { workflowId });
        },
      },
    };
  }, [workflowId, canvas, openRunId, addToast]);

  // ─── Phase 7b-4 — AI Builder JSON Import handler ────────────────
  // Accepts two shapes:
  //   1. Conductor: { name?, tasks: [...], ... }     → mapped via typeMap
  //   2. Canvas:    { nodes: [...], connections: [...] }   → used directly
  //
  // Returns a 'diff' message so the operator confirms before mutating
  // canvas — same UX as a Build response, just no AI round trip.
  //
  // The Conductor mapper is intentionally thin — full-fidelity import
  // remains the BE-driven /workflows/convert path. This client-side
  // shortcut is for "I have JSON, want to see it on canvas now" cases
  // (the V3 pattern at CanvasPageV3.jsx:16027-16107).
  const handleAIImportSubmit = useCallback(async (jsonText) => {
    if (!workflowId) {
      return { summary: 'No workflow loaded — open one first.', kind: 'error' };
    }
    let parsed;
    try {
      parsed = JSON.parse(jsonText);
    } catch (e) {
      throw new Error(`Invalid JSON: ${e.message}`);
    }
    let newNodes, newConns, source;
    if (parsed && Array.isArray(parsed.tasks)) {
      // Conductor shape. Thin mapper — covers the common types; falls
      // back to connector_task for unknown types (same as V3).
      source = 'conductor';
      const typeMap = {
        START: 'manual_start', SWITCH: 'condition', CONNECTOR: 'connector_task',
        HTTP: 'http_task', SCRIPT: 'script_task', NOTIFICATION: 'notification_task',
        DATA_TRANSFORM: 'data_transform', WAIT: 'delay', FORK: 'parallel_fork',
        JOIN: 'parallel_join', SKILL: 'skill_task', AGENT: 'agent_task',
        SIMPLE: 'connector_task',
      };
      const ts = Date.now();
      newNodes = parsed.tasks.map((task, idx) => {
        const ip = task.inputParameters || {};
        const cfg = {};
        // Selectively restore the fields V4 Inspectors read.
        ['connectorType', 'connectorId', 'action', 'skillParams', 'expression',
         'inputMapping', 'errorTypes', 'retryCount', 'fallbackAction', 'script',
         'language', 'trueLabel', 'falseLabel']
          .forEach((k) => { if (ip[k] !== undefined) cfg[k] = ip[k]; });
        if (task.outputVariable) cfg.outputVariable = task.outputVariable;
        return {
          id: task.taskReferenceName || `n_${ts}_${String(idx).padStart(3, '0')}`,
          x: 400,
          y: 80 + idx * 160,
          type: typeMap[task.type] || 'connector_task',
          label: task.name || task.taskReferenceName || `Task ${idx + 1}`,
          config: cfg,
        };
      });
      // Connections: prefer explicit array, fall back to inferred from
      // task.next + decisionCases.
      if (Array.isArray(parsed.connections)) {
        newConns = parsed.connections.map((c) => ({
          from: c.from, to: c.to,
          fromPort: c.fromPort || 'default',
          toPort: c.toPort || 'default',
        }));
      } else {
        newConns = [];
        parsed.tasks.forEach((task) => {
          const fromId = task.taskReferenceName;
          const nexts = task.next ? (Array.isArray(task.next) ? task.next : [task.next]) : [];
          nexts.forEach((toId) => newConns.push({ from: fromId, to: toId, fromPort: 'default', toPort: 'default' }));
          if (task.decisionCases) {
            Object.entries(task.decisionCases).forEach(([port, targets]) => {
              const list = Array.isArray(targets) ? targets : [targets];
              list.forEach((toId) => newConns.push({ from: fromId, to: toId, fromPort: port, toPort: 'default' }));
            });
          }
        });
      }
    } else if (parsed && Array.isArray(parsed.nodes)) {
      // Canvas shape — pass through unchanged.
      source = 'canvas';
      newNodes = parsed.nodes;
      newConns = Array.isArray(parsed.connections) ? parsed.connections : [];
    } else {
      throw new Error('Unrecognized JSON shape — expected { tasks: [...] } (Conductor) or { nodes: [...], connections: [...] } (canvas).');
    }

    const currentCanvas = (typeof canvas.getCanvasData === 'function')
      ? canvas.getCanvasData()
      : { nodes: canvas.nodes || [], connections: canvas.connections || [] };
    const currentIds = new Set((currentCanvas.nodes || []).map((n) => n.id));
    const newIds = new Set(newNodes.map((n) => n.id));
    const keepCount = [...currentIds].filter((id) => newIds.has(id)).length;
    const addCount  = newNodes.length - keepCount;
    const dropCount = currentIds.size  - keepCount;
    trackTelemetry('v4_ai_import', { source, add: addCount, drop: dropCount }, { workflowId });
    return {
      summary: `Parsed ${source === 'conductor' ? 'Conductor' : 'canvas'} JSON — ${newNodes.length} nodes, ${newConns.length} connections.`,
      kind: 'diff',
      payload: {
        addCount,
        dropCount,
        keepCount,
        warnings: [],
        apply: async () => {
          if (typeof canvas.loadCanvasData === 'function') {
            await canvas.loadCanvasData(
              { nodes: newNodes, connections: newConns },
              { autoLayoutOnLoad: true, zoomToFitOnLoad: true },
            );
          }
          addToast('Imported to canvas', 'success');
          trackTelemetry('v4_ai_import_applied', { source }, { workflowId });
        },
        discard: () => {
          trackTelemetry('v4_ai_import_discarded', { source }, { workflowId });
        },
      },
    };
  }, [workflowId, canvas, addToast]);

  // ─── Phase 7c-3 — Right rail mode router ────────────────────────
  // Replaces 4 separate "open" booleans (aiBuilderOpen, validationPanelOpen,
  // etc.) with one enum. The rail renders exactly one active surface.
  // Existing trigger paths still flip their old booleans (validation chip
  // → setValidationPanelOpen(true), ⌘I → setAiBuilderOpen(open=>!open)) —
  // these adapter effects translate that into railMode changes so we
  // don't have to rewire every call site at once.
  const [railMode, setRailMode] = useState(RAIL_MODES.INSPECTOR);

  // ─── Phase 7q-1 — Left panel mode (Nodes palette vs Runs list) ──
  // Defaults to NODES so existing operators land on the palette as
  // before. V4LeftPanel auto-nudges to RUNS on the leading edge of
  // openRunId (new run started) — never against an operator who's
  // manually flipped back to NODES.
  const [leftMode, setLeftMode] = useState(LEFT_MODES.NODES);

  // ─── Phase 7b — AI Builder open state + ⌘I hotkey ──────────────
  // Phase 7c-3 — flips the rail to AI mode (or back to Inspector when
  // the toggle closes). Bridge-state aiBuilderOpen is preserved so any
  // legacy consumer still reads the boolean correctly.
  const [aiBuilderOpen, setAiBuilderOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      const isCmdOrCtrl = e.metaKey || e.ctrlKey;
      if (!isCmdOrCtrl || (e.key !== 'i' && e.key !== 'I')) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      setRailMode((current) => current === RAIL_MODES.AI ? RAIL_MODES.INSPECTOR : RAIL_MODES.AI);
      setAiBuilderOpen((open) => !open);
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, []);

  // ─── Phase 7c-3 — bridge legacy "validationPanelOpen" → rail mode.
  // The validation chip in the new toolbar calls setValidationPanelOpen(true);
  // this effect translates that into a rail-mode switch.
  useEffect(() => {
    if (validationPanelOpen) {
      setRailMode(RAIL_MODES.VALIDATION);
      setValidationPanelOpen(false); // one-shot — rail owns visibility now
    }
  }, [validationPanelOpen]);

  // Auto-prime AI Builder in Debug mode when the operator opens a run
  // detail panel for a FAILED run. We pass the runId through; the
  // builder reads it in its own effect. Reset when openRunId clears.
  const [failedRunIdForAI, setFailedRunIdForAI] = useState(null);
  // Note: we'd ideally fetch the run status here, but the AI builder
  // accepts ANY runId hint — Build/Debug mode auto-selects based on
  // the runId presence. For Phase 7b-1 we just plumb the value; the
  // panel decides what to do with it.

  // Global ⌘K / Ctrl+K hotkey. Toggles the palette. The platform shell
  // (DashboardLayoutV2) has its OWN ⌘K that focuses the top search bar,
  // so we register on the CAPTURE phase and stopImmediatePropagation
  // to win the race — V4's canvas palette is the more useful action
  // when an operator is editing a workflow. Outside V4 the shell's ⌘K
  // still works because this handler is scoped to CanvasPageV4's mount.
  useEffect(() => {
    const onKey = (e) => {
      const isCmdOrCtrl = e.metaKey || e.ctrlKey;
      if (!isCmdOrCtrl || (e.key !== 'k' && e.key !== 'K')) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      setPaletteOpen((open) => !open);
    };
    // capture: true → we run BEFORE the shell's bubble-phase listener.
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, []);

  // ─── Phase 6c-5 — Smart re-layout ───────────────────────────────
  // Counter the host bumps to force V4CanvasView's ELK pipeline to
  // re-run with the current payload. Useful when the operator has
  // session-dragged nodes around and wants to snap back to canonical
  // positions. ELK derives positions purely from the IR, so a re-run
  // overwrites the manual positions (which were never persisted —
  // V4CanvasView.jsx:130 documents this as session-scoped).
  const [relayoutKey, setRelayoutKey] = useState(0);
  const handleRelayout = useCallback(() => {
    setRelayoutKey((k) => k + 1);
    addToast('Canvas re-laid out', 'success');
    trackTelemetry('v4_relayout', {}, { workflowId });
  }, [addToast, workflowId]);

  // Status-flag rendering: only meaningful when a workflow is loaded.
  const readOnlyStatus = useMemo(() => {
    const s = (workflowMeta?.status || '').toUpperCase();
    return s === 'LOCKED' || s === 'ARCHIVED';
  }, [workflowMeta]);

  useEffect(() => {
    // Tear down any pending timer when deps change — we'll re-arm if
    // still dirty. This naturally extends the debounce window each
    // time the user makes another edit, which is the standard auto-
    // save UX (don't keep clobbering the BE mid-typing).
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    if (!dirty) return;
    if (!workflowId || !workflowMeta) return;
    if (loading || saving) return;
    if (readOnlyStatus) return;
    if (createDialog) return;
    autoSaveTimerRef.current = setTimeout(() => {
      // Re-check guards at fire-time to avoid races (the user may have
      // navigated to another workflow during the debounce window).
      if (typeof handleSaveRef.current === 'function') {
        Promise.resolve(handleSaveRef.current({ silent: true })).then(() => {
          setLastSavedAt(Date.now());
        }).catch(() => {/* handleSave already toasts the failure */});
      }
    }, 2000);
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, [dirty, workflowId, workflowMeta, loading, saving, readOnlyStatus, createDialog]);

  // Stable "Saved Xs ago" / "Saving…" status text. Re-renders every
  // 15s so the relative time stays roughly accurate without a tight
  // ticker. Pure derived state — no extra state needed.
  const [nowTick, setNowTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 15000);
    return () => clearInterval(id);
  }, []);
  const autoSaveStatus = useMemo(() => {
    void nowTick; // keep the recompute on tick
    if (saving) return 'Saving…';
    if (dirty)  return 'Unsaved changes';
    if (!lastSavedAt) return null;
    const ago = Math.round((Date.now() - lastSavedAt) / 1000);
    if (ago < 5)   return 'Saved';
    if (ago < 60)  return `Saved ${ago}s ago`;
    const mins = Math.round(ago / 60);
    if (mins < 60) return `Saved ${mins}m ago`;
    const hrs = Math.round(mins / 60);
    return `Saved ${hrs}h ago`;
  }, [saving, dirty, lastSavedAt, nowTick]);

  const v3Href = workflowId ? `/canvas?workflowId=${workflowId}` : '/canvas';

  // Phase 7c-2 — Open in V3 with dirty-prompt. Encapsulated so the
  // toolbar overflow menu can call it as a plain handler. Mirrors the
  // inline onClick that used to live on the <Link>.
  const handleOpenInV3 = useCallback(() => {
    if (dirty && workflowId) {
      const choice = window.confirm(
        'You have unsaved changes in V4.\n\n'
        + 'OK  = Save first, then open in V3\n'
        + 'Cancel = Open in V3 anyway (V4 changes will be lost)'
      );
      trackTelemetry('v4_open_in_v3', {
        dirty: true,
        user_choice: choice ? 'save_first' : 'discard',
        has_workflow: true,
      }, { workflowId });
      if (choice && typeof handleSaveRef.current === 'function') {
        handleSaveRef.current().then(() => { window.location.href = v3Href; });
      } else {
        window.location.href = v3Href;
      }
      return;
    }
    trackTelemetry('v4_open_in_v3', { dirty: false, has_workflow: !!workflowId }, { workflowId });
    window.location.href = v3Href;
  }, [dirty, workflowId, v3Href]);

  return (
    // VIEWPORT-FIT (Phase 6c-1 follow-up) — use 100% of the parent
    // (DashboardLayoutV2's `<main>` flex column) rather than 100vh.
    // 100vh ignores the shell's 56px header, pushing the canvas (and
    // therefore the bottom-right MiniMap) 56px below the viewport.
    // 100% respects the shell's allocated height — keeps the mini-map
    // and pagination controls visible without scroll.
    <div style={{
      display: 'flex', flexDirection: 'column',
      height: '100%', minHeight: 0, background: '#f8fafc',
    }}>
      {/* ── Top bar — Phase 7c-2 redesign ────────────────────────
       * Replaced the inline header (7 buttons + 3 statuses crammed in
       * one row) with V4Toolbar — grouped IDENTITY · STATUS · ACTIONS
       * zones, V2 tokens, overflow menu for low-frequency actions.
       * The old block is preserved below in case we need to roll back. */}
      <V4Toolbar
        workflowId={workflowId}
        workflowMeta={workflowMeta}
        orgId={orgId}
        tenantId={tenantId}
        loading={loading}
        saving={saving}
        dirty={dirty}
        executing={executing}
        autoSaveStatus={autoSaveStatus}
        validation={validation}
        lastRunId={lastRunId}
        onRun={handleRun}
        onSwitchWorkflow={handleSwitchWorkflow}
        onNew={openNewDialog}
        onSaveAs={openSaveAsDialog}
        onSave={handleSave}
        onRelayout={handleRelayout}
        onOpenInV3={handleOpenInV3}
        onSelectRun={(runId) => setOpenRunId(runId)}
        onOpenValidation={() => setValidationPanelOpen(true)}
        v3Href={v3Href}
      />

      {/* ── Old header block — kept commented for rollback safety ─ */}
      {false && (
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 20px',
        background: '#ffffff',
        borderBottom: '1px solid #e2e8f0',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <span style={{
            fontSize: 10, fontWeight: 700,
            color: '#7c3aed', background: '#ede9fe',
            padding: '3px 8px', borderRadius: 999,
            letterSpacing: 0.6, textTransform: 'uppercase',
            whiteSpace: 'nowrap',
          }}>
            V4 · Phase 1 beta
          </span>
          <V4WorkflowSwitcher
            currentWorkflowId={workflowId}
            currentWorkflowName={workflowMeta?.name}
            onSelect={handleSwitchWorkflow}
            onNew={openNewDialog}
            onSaveAs={openSaveAsDialog}
            canSaveAs={!!workflowId}
            orgId={orgId}
            tenantId={tenantId}
          />
          {loading && (
            <span style={{ fontSize: 11, color: '#64748b' }}>Fetching definition…</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Phase 7a-1 — Run button. Wires api.executeWorkflow. Gated
           * on workflow having no validation errors (Phase 5e). Auto-
           * saves dirty workflows before run so the BE executes what
           * the operator sees. Hidden when no workflow loaded. */}
          {workflowId && (
            <button
              type="button"
              onClick={handleRun}
              disabled={executing || loading || saving}
              title={
                (validation?.stats?.error || 0) > 0
                  ? 'Fix validation errors before running'
                  : 'Run workflow now'
              }
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '5px 12px',
                fontSize: 11, fontWeight: 600,
                color: '#ffffff',
                background: executing ? '#94a3b8' : '#15803d',
                border: 'none',
                borderRadius: 6,
                cursor: (executing || loading || saving) ? 'not-allowed' : 'pointer',
                opacity: (executing || loading || saving) ? 0.7 : 1,
                fontFamily: 'inherit',
                transition: 'background 160ms ease',
              }}
            >
              {/* Right-pointing triangle for play */}
              <span aria-hidden="true" style={{ fontSize: 9 }}>▶</span>
              {executing ? 'Running…' : 'Run'}
            </button>
          )}
          {/* Phase 7a-2 — recent runs dropdown. Lazily fetches when
           * opened + auto-refreshes when a fresh run starts (lastRunId
           * changes). Selecting a row opens the 7a-3 detail panel. */}
          {workflowId && (
            <V4RunsDropdown
              workflowId={workflowId}
              freshRunId={lastRunId}
              onSelectRun={(runId) => setOpenRunId(runId)}
            />
          )}
          {/* Phase 6c-5 — Re-layout toolbar button. Snaps the canvas
           * back to canonical ELK positions after a session of manual
           * drags. Hidden when no workflow is loaded (nothing to lay
           * out) and when loading (avoid spinning ELK on an in-flight
           * payload swap). */}
          {workflowId && !loading && (
            <button
              type="button"
              onClick={handleRelayout}
              title="Re-layout — snap nodes back to canonical positions"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '5px 10px', fontSize: 11, fontWeight: 500,
                color: '#0f172a',
                background: '#ffffff',
                border: '0.5px solid #cbd5e1',
                borderRadius: 6,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Re-layout
            </button>
          )}
          {/* Phase 6c-2 — Auto-save status. Only rendered when a workflow
           * is loaded. "Saving…" while in-flight, "Unsaved changes" while
           * dirty (the orange Save button already says the same thing,
           * but this label makes it explicit), otherwise the relative
           * "Saved 12s ago" timestamp. */}
          {workflowId && autoSaveStatus && (
            <span
              role="status"
              aria-live="polite"
              style={{
                fontSize: 11,
                color: dirty ? '#9a3412' : '#64748b',
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
              }}
            >
              {autoSaveStatus}
            </span>
          )}
          {workflowId && (
            <button
              type="button"
              onClick={() => handleSave()}
              disabled={saving || loading}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '6px 12px', fontSize: 12, fontWeight: 500,
                color: '#ffffff',
                // Phase 4a — when dirty, swap to brand coral so the
                // operator's eye is drawn there. Stays black otherwise.
                background: dirty && !saving ? '#E96B3F' : '#0f172a',
                border: 'none', borderRadius: 6,
                cursor: (saving || loading) ? 'not-allowed' : 'pointer',
                opacity: (saving || loading) ? 0.6 : 1,
                transition: 'background 160ms ease',
              }}
              title={dirty ? 'Unsaved changes' : 'No unsaved changes'}
            >
              <SaveIcon size={12} />
              {saving ? 'Saving…' : (dirty ? 'Save •' : 'Save')}
            </button>
          )}
          <Link
            to={v3Href}
            onClick={(e) => {
              // Phase 4c — smart open. If the workflow is dirty, intercept
              // the click and offer a save-first path. confirm() is the MVP;
              // a custom dialog can replace this in a polish pass without
              // changing the contract.
              if (dirty && workflowId) {
                e.preventDefault();
                const choice = window.confirm(
                  'You have unsaved changes in V4.\n\n'
                  + 'OK  = Save first, then open in V3\n'
                  + 'Cancel = Open in V3 anyway (V4 changes will be lost)'
                );
                trackTelemetry('v4_open_in_v3', {
                  dirty: true,
                  user_choice: choice ? 'save_first' : 'discard',
                  has_workflow: true,
                }, { workflowId });
                if (choice) {
                  handleSave().then(() => { window.location.href = v3Href; });
                } else {
                  window.location.href = v3Href;
                }
                return;
              }
              trackTelemetry('v4_open_in_v3', { dirty: false, has_workflow: !!workflowId }, { workflowId });
            }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 12px', fontSize: 12, fontWeight: 500,
              color: '#0f172a', background: '#f1f5f9',
              borderRadius: 6, textDecoration: 'none',
              border: '1px solid #e2e8f0',
            }}
            title="Open this workflow in the V3 canvas (full feature set)"
          >
            <ExternalLink size={12} /> Open in V3
          </Link>
        </div>
      </header>
      )}

      {/* ── Phase 4c — V4-limitations banner ───────────────────────
       * Surfaces when the loaded workflow uses features V4 doesn't fully
       * support (ExteLet vendor editor, Cognitive Brief slideout, runbook
       * context, AutoLet locks). Per-feature tooltip explains the gap so
       * the operator knows exactly why they should jump to V3. Amber tone
       * matches the operator's mental model for "watch out, but not broken." */}
      {workflowId && v4Limitations.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '10px 20px',
          background: '#fef3c7',
          borderBottom: '1px solid #fcd34d',
          fontSize: 12, color: '#78350f',
          flexShrink: 0,
        }}>
          <AlertTriangle size={16} style={{ color: '#d97706', flexShrink: 0, marginTop: 1 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              This workflow uses features V4 doesn’t fully support yet
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px', alignItems: 'center' }}>
              {v4Limitations.map((l) => (
                <span
                  key={l.id}
                  title={l.why}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '2px 8px',
                    background: 'rgba(217, 119, 6, 0.12)',
                    border: '0.5px solid rgba(217, 119, 6, 0.35)',
                    borderRadius: 4,
                    cursor: 'help',
                  }}
                >
                  {l.label}
                </span>
              ))}
              <Link
                to={v3Href}
                onClick={() => trackTelemetry('v4_open_in_v3_from_banner', { limitations: v4Limitations.map((l) => l.id) }, { workflowId })}
                style={{
                  marginLeft: 4,
                  color: '#78350f', fontWeight: 600,
                  textDecoration: 'underline',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}
              >
                Open in V3 for full editing <ExternalLink size={10} />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* ── Phase 5f-4 — orphan-repair banner ───────────────────────
       * Surfaces useCanvas's last-load repair report. The "repairs"
       * array carries human-readable strings like "Connected orphan
       * node n_abc after Start" — we render the count compactly and
       * let the operator expand to see each. Dismissible. Auto-shows
       * again on the next workflow load that triggers more repairs. */}
      {workflowId && canvas.orphanRepairReport && !orphanRepairDismissed && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '10px 20px',
          background: '#eff6ff',
          borderBottom: '1px solid #bfdbfe',
          fontSize: 12, color: '#1e3a8a',
          flexShrink: 0,
        }}>
          <AlertCircle size={16} style={{ color: '#3b82f6', flexShrink: 0, marginTop: 1 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              Auto-repaired {canvas.orphanRepairReport.count} graph issue
              {canvas.orphanRepairReport.count === 1 ? '' : 's'} on load
            </div>
            <div style={{ fontSize: 11, color: '#1e40af', lineHeight: 1.5 }}>
              The workflow had orphan nodes or dangling edges. Prove7 wired
              them in deterministically so the canvas renders cleanly.
              {' '}
              <button
                type="button"
                onClick={() => setOrphanRepairExpanded((v) => !v)}
                style={{
                  background: 'transparent', border: 'none', padding: 0,
                  color: '#1e40af', fontWeight: 600, cursor: 'pointer',
                  textDecoration: 'underline', fontSize: 11,
                  fontFamily: 'inherit',
                }}
              >
                {orphanRepairExpanded ? 'Hide details' : 'View details'}
              </button>
            </div>
            {orphanRepairExpanded && (
              <ul style={{
                margin: '6px 0 0', padding: '6px 0 0 16px',
                listStyle: 'disc', fontSize: 10.5, color: '#1e40af',
                lineHeight: 1.6,
                maxHeight: 140, overflowY: 'auto',
              }}>
                {(canvas.orphanRepairReport.repairs || []).map((r, i) => (
                  <li key={i}><code style={{
                    background: 'rgba(59, 130, 246, 0.08)',
                    padding: '0 4px', borderRadius: 3,
                    fontFamily: 'SFMono-Regular, Menlo, monospace',
                    fontSize: 10,
                  }}>{r}</code></li>
                ))}
              </ul>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              setOrphanRepairDismissed(true);
              trackTelemetry('v4_orphan_repair_dismissed', {
                repair_count: canvas.orphanRepairReport.count,
              }, { workflowId });
            }}
            aria-label="Dismiss"
            style={{
              flexShrink: 0,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 22, height: 22, padding: 0,
              background: 'rgba(59, 130, 246, 0.10)',
              border: '0.5px solid rgba(59, 130, 246, 0.30)',
              borderRadius: 4, color: '#1e40af', cursor: 'pointer',
              fontFamily: 'inherit',
            }}
            title="Dismiss until next workflow load"
          >
            ×
          </button>
        </div>
      )}

      {/* ── Phase 5e — validation banner ───────────────────────────
       * Sits below the v4Limitations banner (when present) and above
       * the canvas surface. Hidden when validation.stats.total === 0
       * — clean workflow gets no banner noise. */}
      {workflowId && (
        <V4ValidationBanner
          stats={validation.stats}
          expanded={validationPanelOpen}
          onToggle={() => {
            const next = !validationPanelOpen;
            setValidationPanelOpen(next);
            trackTelemetry('v4_validation_panel_toggle', { open: next }, { workflowId });
          }}
        />
      )}

      {/* ── Canvas surface ──────────────────────────────────────── */}
      <div style={{ flex: 1, position: 'relative', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Phase 7d cleanup — Canvas/JSON tab strip REMOVED. The JSON
         * view is now exclusively a right-rail mode (V4RightRail JSON
         * icon). V3 used a single right-side panel for definition JSON
         * (CanvasPageV3 ExecutionDetailPanel definition tab); the
         * original V4 Phase 5d-1 tab approach caused a full-page
         * takeover that competed with the rail's JSON mode, confusing
         * operators with two parallel surfaces showing identical data. */}
        {workflowId ? (
          <>
            {/* Phase 5a-1 — flex split: canvas takes remaining width,
             * Inspector pins 320px to the right. The flex container is
             * the existing canvas-surface div (already position: relative
             * minHeight: 0, flex: 1) — we wrap canvas + inspector in
             * row-flex inside it.
             *
             * Phase 7d cleanup — canvas always rendered (no Canvas/JSON
             * tab gate). JSON view moved to right-rail-only. */}
            <div style={{
              display: 'flex', flexDirection: 'row',
              width: '100%', flex: 1, minHeight: 0,
              position: 'relative',
            }}>
              {/* Phase 7q-1 — left panel router. Icon strip toggles
               * between the Phase 5b node palette and the new V3-parity
               * Runs tab. Defaults to NODES — zero regression for the
               * existing palette-first workflow. */}
              <V4LeftPanel
                mode={leftMode}
                onModeChange={setLeftMode}
                onAddOrphan={handlePaletteAdd}
                hiddenTypes={paletteHiddenTypes}
                workflowId={workflowId}
                freshRunId={lastRunId}
                openRunId={openRunId}
                onSelectRun={(runId) => { setOpenRunId(runId); }}
                onToast={addToast}
              />
              {/* Phase 7c-7 — center column: canvas on top, bottom dock
                * underneath (auto-mounted when a run is in flight). The
                * dock implements the B-half of the A+B hybrid design:
                * Calm Pro chrome by default, Mission Control's horizontal
                * execution dock when openRunId is set. */}
              <div style={{
                flex: 1, minWidth: 0,
                display: 'flex', flexDirection: 'column',
              }}>
                <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
                  <V4CanvasView
                    payload={payload}
                    onConnect={handleConnect}
                    onSelectNode={handleSelectNode}
                    /* Phase 7h — only cognitive_loop has a meaningful
                     * double-click action today (open Cognitive Brief
                     * slideout). Other types no-op inside the handler
                     * so single-click selection isn't disturbed. */
                    onNodeDoubleClick={handleNodeDoubleClick}
                    onAddAfter={handleAddAfter}
                    onDeleteSelected={handleDeleteSelected}
                    onDeleteMany={handleDeleteMany}
                    selectedNodeId={canvas.selected}
                    onUndo={handleUndo}
                    onRedo={handleRedo}
                    onCopy={handleCopy}
                    onPaste={handlePaste}
                    validationByNodeId={validationByNodeId}
                    /* Phase 6c-5 — bump to force ELK re-layout. */
                    relayoutKey={relayoutKey}
                  />
                </div>
                {openRunId && (
                  <V4BottomDock
                    runId={openRunId}
                    onClose={() => setOpenRunId(null)}
                    onJumpToNode={handleJumpToNode}
                    selectedCanvasNodeId={canvas.selected || null}
                  />
                )}
              </div>
              {/* Phase 7c-3 — single right rail replaces 4 floating panels.
                * One active mode at a time (Inspector / Validation / Run /
                * AI / JSON). Mode is controlled here so the existing trigger
                * paths (validation chip click, ⌘I, Run-row click) flip it. */}
              <V4RightRail
                mode={railMode}
                onModeChange={setRailMode}
                /* Phase 7c-7 — when bottom dock is showing run detail,
                 * hide the rail's Run mode to avoid duplicate render. */
                hideRunMode={!!openRunId}
                selectedNode={(canvas.nodes || []).find((n) => n.id === canvas.selected) || null}
                onNodeUpdate={(id, patch) => {
                  if (typeof canvas.updateNode === 'function') canvas.updateNode(id, patch);
                }}
                onNodeDelete={(id) => {
                  if (typeof canvas.deleteNode === 'function') canvas.deleteNode(id);
                  trackTelemetry('v4_delete', { source: 'inspector', type: (canvas.nodes || []).find((n) => n.id === id)?.type }, { workflowId });
                }}
                readOnly={readOnlyStatus}
                validation={validation}
                onJumpToNode={handleJumpToNode}
                openRunId={openRunId}
                onRunClose={() => setOpenRunId(null)}
                aiBuilderProps={{
                  workflowId,
                  workflowName: workflowMeta?.name,
                  failedRunId: failedRunIdForAI,
                  readOnly: readOnlyStatus,
                  onSubmitBuild: handleAIBuildSubmit,
                  onSubmitDebug: handleAIDebugSubmit,
                  onSubmitImport: handleAIImportSubmit,
                }}
                payload={payload}
              />
            </div>
            {/* Phase 7d cleanup — full-page JSON view removed. The JSON
             * Code view is now ONLY accessible via the right rail's
             * `{}` mode icon (V4RightRail → RAIL_MODES.JSON). The old
             * Canvas/JSON tab strip and full-page takeover (which
             * duplicated the right-rail panel) was confusing operators.
             * V3 used a single right-side panel for definition JSON;
             * V4 now mirrors that pattern. */}
            {/* Phase 4a — keyboard shortcut hint strip.
             * Floats bottom-left of the canvas, low-opacity. Doesn't
             * collide with ReactFlow's bottom-center Controls toolbar.
             * Operators discover shortcuts here without consulting docs.
             * Phase 7d cleanup — always rendered (no Canvas/JSON tab
             * gate; JSON view moved to right-rail). */}
            <div
              aria-label="Keyboard shortcuts"
              style={{
                position: 'absolute',
                left: 16, bottom: 16,
                display: 'flex', gap: 12, alignItems: 'center',
                padding: '6px 10px',
                background: 'rgba(255, 255, 255, 0.92)',
                border: '0.5px solid rgba(0, 0, 0, 0.08)',
                borderRadius: 6,
                fontSize: 10.5, color: '#64748b',
                fontFamily: 'SFMono-Regular, Menlo, monospace',
                lineHeight: 1,
                pointerEvents: 'none', // never block canvas drags
                userSelect: 'none',
                zIndex: 5,
              }}
            >
              <span><kbd style={shortcutKbdStyle}>⌘Z</kbd> Undo</span>
              <span><kbd style={shortcutKbdStyle}>⌘⇧Z</kbd> Redo</span>
              <span><kbd style={shortcutKbdStyle}>⌘C</kbd> Copy</span>
              <span><kbd style={shortcutKbdStyle}>⌘V</kbd> Paste</span>
              <span><kbd style={shortcutKbdStyle}>⌫</kbd> Delete</span>
              <span><kbd style={shortcutKbdStyle}>⇧+click</kbd> Multi-select</span>
            </div>
          </>
        ) : (
          <div style={{
            height: '100%', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{ textAlign: 'center', maxWidth: 520, padding: 24 }}>
              <AlertCircle size={36} color="#94a3b8" />
              <h2 style={{
                fontSize: 18, fontWeight: 600, margin: '14px 0 6px',
                color: '#0f172a',
              }}>
                No workflow loaded
              </h2>
              <p style={{
                fontSize: 13, color: '#64748b', margin: '0 0 16px',
                lineHeight: 1.6,
              }}>
                Open an existing workflow from the dropdown in the header, or create
                a fresh one. Drag-persistence, manual edges, undo/redo, and the node
                palette all work out of the box.
              </p>
              <div style={{ display: 'inline-flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={openNewDialog}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '8px 14px', fontSize: 13, fontWeight: 500,
                    color: '#ffffff', background: '#0f172a',
                    border: 'none', borderRadius: 6, cursor: 'pointer',
                  }}
                >
                  <SaveIcon size={14} /> New workflow
                </button>
                <Link
                  to="/canvas"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '8px 14px', fontSize: 13, fontWeight: 500,
                    color: '#0f172a', background: '#f1f5f9',
                    borderRadius: 6, textDecoration: 'none',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <ExternalLink size={14} /> Pick in V3
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Phase 5c-2/3 — name dialog for New + Save As ───────────── */}
      <V4WorkflowCreateDialog
        isOpen={!!createDialog}
        mode={createDialog?.mode}
        defaultName={createDialog?.defaultName}
        busy={createBusy}
        onClose={() => { if (!createBusy) setCreateDialog(null); }}
        onConfirm={handleCreateConfirm}
      />

      {/* ── Phase 2a — TaskPicker overlay ────────────────────────────
       * Reuses V3's picker component verbatim. Conditional render gated
       * on insertionTarget so the picker mounts/unmounts cleanly on each
       * open (matches V3's lifecycle pattern). Anchor coords are arbitrary
       * for Phase 2a (the picker self-centers); Phase 2b will compute
       * per-target anchor coords for visual alignment with the source. */}
      {insertionTarget && workflowId && (
        <TaskPicker
          isOpen
          target={insertionTarget}
          anchor={{ x: 400, y: 200 }}
          nodes={canvas.nodes || []}
          connections={canvas.connections || []}
          installedConnectors={{}}
          marketplaceConnectors={[]}
          agents={[]}
          recipes={getCuratedRecipes()}
          onChoose={handlePickerChoose}
          onClose={() => {
            setInsertionTarget(null);
            // Phase 4b — distinguishes intentional dismiss from successful
            // insertion (the latter fires row_chosen via handlePickerChoose).
            trackTelemetry('picker_close', { target_kind: insertionTarget?.kind }, { workflowId });
          }}
        />
      )}

      {/* ── Phase 6c-3 — Command palette (⌘K) ────────────────────────
       * Mounted last so it overlays everything else. The items list is
       * computed inline rather than memoized at the top of the
       * component to keep this large refactor minimally invasive — the
       * palette only re-renders when paletteOpen flips, so the cost is
       * negligible. */}
      <V4CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        items={buildPaletteItems({
          // workflow-scoped actions
          workflowId,
          dirty,
          handleSave,
          openSaveAsDialog,
          openNewDialog,
          handleUndo,
          handleRedo,
          handleRelayout,
          openValidationPanel: () => setValidationPanelOpen(true),
          // node insert (uses canvas.addTaskToFlow with no anchor → cascade
          // picks the best slot; same path the palette's click-to-add uses)
          canvas,
          addToast,
          // workflow switcher
          paletteWorkflows,
          handleSwitchWorkflow,
          currentWorkflowId: workflowId,
        })}
      />

      {/* ── Phase 7g — Execute Input Modal ─────────────────────────
       * Active when handleRun resolves required inputs and finds at
       * least one. The modal's Submit calls runWithInputs with the
       * trimmed values; Cancel clears the prompt without running. */}
      <V4ExecuteInputModal
        open={!!executeInputPrompt}
        params={executeInputPrompt?.params || []}
        values={executeInputPrompt?.values || {}}
        onChange={(next) => setExecuteInputPrompt((prev) => prev && ({ ...prev, values: next }))}
        onCancel={() => setExecuteInputPrompt(null)}
        onSubmit={(trimmed) => {
          setExecuteInputPrompt(null);
          runWithInputs(trimmed);
        }}
      />

      {/* ── Phase 7h — Cognitive Brief slideout ────────────────────
       * Opens when the operator double-clicks a cognitive_loop node
       * (handleNodeDoubleClick gates the trigger to that type). Owns
       * its own form state and validates locally; host owns the async
       * PUT /agents/{id} via handleSaveCognitiveBrief. */}
      <V4CognitiveBriefSlideout
        open={briefSlideoutOpen}
        agent={linkedAgent}
        saving={briefSaving}
        onSave={handleSaveCognitiveBrief}
        onCancel={() => { if (!briefSaving) setBriefSlideoutOpen(false); }}
      />

      {/* ── Phase 7i — Governance diff modal ───────────────────────
       * Opens automatically when handleSave gets back 409/423 from
       * api.updateWorkflow. Shows the BE's structural diff (adds /
       * removes / mutates) and lets the operator regress the linked
       * agent's phase + re-fire the save. Cancel is non-destructive
       * (pending edits stay on the canvas; the BE never wrote them). */}
      <V4GovernanceDiffModal
        open={!!diffModal}
        diff={diffModal}
        agent={linkedAgent}
        regressing={regressing}
        resaving={resaving}
        onRegress={handleRegressFromDiffModal}
        onCancel={() => { if (!regressing && !resaving) setDiffModal(null); }}
      />
    </div>
  );
}

/**
 * buildPaletteItems — Phase 6c-3.
 *
 * Pure function that assembles the {kind, label, subtitle, run, ...}
 * array the V4CommandPalette consumes. Kept outside the component body
 * so it stays cheap to call and easy to unit-test.
 *
 * Three categories appear, ordered Actions → Insert → Switch Workflow:
 *
 *   1. ACTIONS — Save, Save As, New, Undo, Redo, Re-layout, Validation.
 *      Gated by whether a workflow is loaded.
 *   2. INSERT  — Every entry in NODE_TYPES except palette-hidden ones.
 *      Each item invokes canvas.addTaskToFlow(type, label) which uses
 *      the G397 anchor-cascade to splice on the best edge.
 *   3. WORKFLOWS — Other workflows visible to this tenant. Selecting
 *      one calls handleSwitchWorkflow (which respects the dirty prompt).
 */
function buildPaletteItems(ctx) {
  const items = [];
  const {
    workflowId, dirty,
    handleSave, openSaveAsDialog, openNewDialog,
    handleUndo, handleRedo, handleRelayout, openValidationPanel,
    canvas, addToast,
    paletteWorkflows, handleSwitchWorkflow, currentWorkflowId,
  } = ctx;

  // ── Actions ────────────────────────────────────────────────────
  if (workflowId) {
    items.push({
      id: 'act:save',
      kind: 'action',
      label: 'Save workflow',
      subtitle: dirty ? 'Has unsaved changes' : 'No unsaved changes',
      shortcut: '⌘S',
      iconChar: '💾',
      keywords: ['save', 'persist', 'write'],
      run: () => handleSave && handleSave(),
    });
    items.push({
      id: 'act:save-as',
      kind: 'action',
      label: 'Save As… (clone)',
      subtitle: 'Create a new workflow from the current canvas',
      iconChar: '📋',
      keywords: ['copy', 'clone', 'duplicate'],
      run: () => openSaveAsDialog && openSaveAsDialog(),
    });
    items.push({
      id: 'act:undo',
      kind: 'action',
      label: 'Undo',
      shortcut: '⌘Z',
      iconChar: '↶',
      keywords: ['back', 'revert'],
      run: () => handleUndo && handleUndo(),
    });
    items.push({
      id: 'act:redo',
      kind: 'action',
      label: 'Redo',
      shortcut: '⌘⇧Z',
      iconChar: '↷',
      keywords: ['forward'],
      run: () => handleRedo && handleRedo(),
    });
    items.push({
      id: 'act:relayout',
      kind: 'action',
      label: 'Re-layout canvas',
      subtitle: 'Snap nodes back to canonical positions',
      iconChar: '🧭',
      keywords: ['layout', 'arrange', 'align', 'reset', 'positions'],
      run: () => handleRelayout && handleRelayout(),
    });
    items.push({
      id: 'act:validation',
      kind: 'action',
      label: 'Open validation panel',
      subtitle: 'List of workflow issues with jump-to-node',
      iconChar: '⚠️',
      keywords: ['errors', 'warnings', 'lint', 'check'],
      run: () => openValidationPanel && openValidationPanel(),
    });
  }
  items.push({
    id: 'act:new',
    kind: 'action',
    label: 'New workflow…',
    subtitle: 'Create a blank Start → End workflow',
    iconChar: '✨',
    keywords: ['create', 'fresh', 'blank'],
    run: () => openNewDialog && openNewDialog(),
  });

  // ── Insert Node ────────────────────────────────────────────────
  if (workflowId && canvas && typeof canvas.addTaskToFlow === 'function') {
    const seen = new Set();
    for (const [type, def] of Object.entries(NODE_TYPES || {})) {
      if (!def || def.paletteHidden) continue;
      // Skip aliases — their canonical entry covers the type.
      if (def.aliasOf) continue;
      // Don't re-insert sentinels via the palette — Start/End are
      // bookends, not insertable tasks.
      if (type === 'manual_start' || type === 'start' || type === 'end' || type === 'terminate') continue;
      if (seen.has(type)) continue;
      seen.add(type);
      items.push({
        id: `ins:${type}`,
        kind: 'insert',
        label: def.label || type,
        subtitle: def.description || type,
        iconChar: '➕',
        keywords: [type, def.category, ...(def.tags || [])].filter(Boolean),
        run: () => {
          try {
            const res = canvas.addTaskToFlow(type, def.label || type, {}, null, null);
            if (res && res.toast) addToast(res.toast, 'success');
          } catch (e) {
            addToast(`Insert failed: ${e?.message || String(e)}`, 'error');
          }
        },
      });
    }
  }

  // ── Switch Workflow ────────────────────────────────────────────
  if (Array.isArray(paletteWorkflows) && paletteWorkflows.length > 0) {
    for (const w of paletteWorkflows) {
      if (!w || !w.id) continue;
      if (w.id === currentWorkflowId) continue; // current workflow: skip
      items.push({
        id: `wf:${w.id}`,
        kind: 'workflow',
        label: w.name || w.id,
        subtitle: w.status ? `Status: ${w.status}` : undefined,
        iconChar: '↗',
        keywords: [w.name, w.id, w.status].filter(Boolean),
        run: () => handleSwitchWorkflow && handleSwitchWorkflow(w.id),
      });
    }
  }

  return items;
}
